import { Plus, Trash2 } from 'lucide-react';
import { useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermission } from '../../../../auth/AuthContext';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import type { MenuItem } from '../../../../components/Menu';
import { ApiError } from '../../../../lib/apiError';
import {
  inventoryCategoriesApi,
  inventoryItemTypesApi,
  inventoryItemsApi,
  storageFacilitiesApi,
  unitsOfMeasureApi,
} from '../inventoryApi';
import type { InventoryItemDetail } from '../inventoryTypes';
import { InventoryItemModal } from '../components/InventoryItemModal';
import { IssuanceModal } from '../components/IssuanceModal';
import { StockAdjustmentModal } from '../components/StockAdjustmentModal';
import type { StockAdjustmentTarget } from '../components/StockAdjustmentModal';
import { LicenseRegisterModal } from '../components/LicenseRegisterModal';
import type { LicenseRegisterTarget } from '../components/LicenseRegisterModal';
import { StockUnitsRegisterModal } from '../components/StockUnitsRegisterModal';
import type { StockUnitsRegisterTarget } from '../components/StockUnitsRegisterModal';
import { StockDetailModal } from '../components/StockDetailModal';
import type { StockDetailTab } from '../components/StockDetailModal';
import { RecordReturnModal } from '../components/RecordReturnModal';
import { SetStockUnitConditionModal } from '../components/SetStockUnitConditionModal';
import { issuancesApi } from '../issuanceApi';
import type { IssuanceDetail } from '../issuanceTypes';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type {
  ColumnDef,
  FacetValue,
  IndexQueryState,
  NavSlice,
} from '../../../../components/index-page/types';
import { StackedCell } from '../../../../components/table/StackedCell';
import { StatusText } from '../../../../components/table/StatusText';
import { Tooltip } from '../../../../components/Tooltip';
import { TextCell } from '../../../../components/table/TextCell';
import { DateCell } from '../../../../components/table/DateCell';
import { formatDateOnly } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import { itemIdentityOrName } from '../../../../lib/itemIdentity';
import { StockLevelAlerts } from '../components/StockLevelAlerts';
import { stockLevelShortcutItems } from '../components/stockLevelShortcuts';
import { pendingGroupRow, StockLevelsIndexTable } from '../components/StockLevelsIndexTable';
import { StockFigureContext } from '../stockFigureCell';
import { stockLevelsApi } from '../stockApi';
import { ITEM_KIND, type InventoryItemKind } from '../inventoryTypes';
import type {
  StockLevelListItem,
  StockLevelListQuery,
  StockUnitListItem,
} from '../stockTypes';
import { STOCK_STATUS, STOCK_STATUS_LABEL, stockStatusTone } from '../stockTypes';

/**
 * What is on hand, available, low and near expiry — derived, per item.
 *
 * **Nothing on this page is a record.** Every figure is computed at read time from the batches,
 * the units and the ledger, so `stock-levels` itself offers only `read`: the row has no delete,
 * no Clear data and no profile behind its anchor, and none of those is an omission.
 *
 * **What it DOES write belongs to other modules, and carries their keys.** Create registers an
 * inventory item, the group kebab adds one and retires a type, and a row's Stock adjustment
 * corrects a count — `inventory-items.*`, `inventory-item-types.delete`,
 * `stock-batches.create`. A
 * derived view may act on the records it is a view OF; what it must not do is invent a key of
 * its own for them.
 *
 * **It has its OWN table**, reached through `<IndexPage>`'s `renderTable` — the one dedicated
 * table surface in the product. Three things here are shapes the shared table refuses: rows
 * GROUP under a collapsible parent that sums its children, the anchor is a stacked cell, and
 * the row menu sits inside the first column because a pinned trailing gutter would be a
 * horizontal scroll away from the row it acts on. Everything else — breadcrumb, folder nav,
 * toolbar, facets, rail, cog, pager — is still the shell's.
 *
 * **The folder nav files by KIND**, which is what the source system's five tabs do. There is no
 * status axis at all: a level is a view of a LIVE item, so there is nothing deleted to show and
 * no Show deleted to offer.
 */
/**
 * Whether every row in a group answers a question the same way.
 *
 * The group kebab seeds a registration from what the rows under it already agree about, and
 * `undefined` on a DISAGREEMENT is the whole point: an item type can be unscoped, so two items
 * under one governed name may legitimately sit in different categories. Seeding one of them
 * would be the form answering on the reader's behalf with a value half the group contradicts.
 *
 * An EMPTY group answers false, which keeps the callers honest — they index `items[0]`, and a
 * group with no rows has none. It cannot arise today (a group exists because rows filed under
 * it do) and costing one comparison to not depend on that is the cheaper side of the trade.
 */
function unanimous<T>(rows: StockLevelListItem[], of: (row: StockLevelListItem) => T): boolean {
  return rows.length > 0 && rows.every((row) => of(row) === of(rows[0]));
}

/**
 * The unit the most of a group's items are counted in.
 *
 * <b>It is a SUGGESTION and it is not the "latest" entry, which this row cannot answer.</b> A
 * stock level is a derived view with no creation instant on it at all — `otherInfo` returns
 * null and `auditColumns` is false, deliberately — so there is no way to say which item under a
 * group was registered last without putting provenance on a row that has none. The most-used
 * unit needs no new field and is the better answer anyway: it is what the group is counted in,
 * rather than what one item happened to be filed as.
 *
 * Ties break on the first seen, which is arbitrary and harmless — the field is a suggestion the
 * reader can change, not a value they are held to.
 */
function commonUnitCode(rows: StockLevelListItem[]): string | undefined {
  const counts = new Map<string, number>();

  for (const row of rows) {
    if (row.unitCode) counts.set(row.unitCode, (counts.get(row.unitCode) ?? 0) + 1);
  }

  let best: string | undefined;
  let seen = 0;

  for (const [code, count] of counts) {
    if (count > seen) {
      best = code;
      seen = count;
    }
  }

  return best;
}

export function StockLevelsIndexPage() {
  const navigate = useNavigate();

  // The one chip with a module behind it carries that module's own key, so a reader without
  // it meets a disabled chip that says so rather than one that 403s.
  const canReadUnits = usePermission('stock-units.read');
  const canIssue = usePermission('issuances.create');

  /*
    The GROUP row's two actions. Both address the item TYPE rather than any one level, so both
    carry `inventory-item-types` / `inventory-items` keys rather than this page's own.
  */
  // The tab's kind travels with the open, so the form can lock it — see `SLICE_KIND`.
  const [creating, setCreating] = useState<{ kind?: InventoryItemKind } | null>(null);

  /*
    THE GROUP a registration was started from — its type, and whatever else the rows already
    filed under it AGREE about.

    A group is an item type, and the items under it normally share a category and a kind as
    well. Seeding only the type left the reader re-answering two questions the row they pressed
    had already answered — and worse, the category is the field the type picker is NARROWED by,
    so a locked type sat above an empty category.

    The two extra fields are `undefined` where the group's rows DISAGREE, which is a real state:
    an item type can be unscoped, so two items under one name may legitimately sit in different
    categories. Seeding one of them would be the form answering on the reader's behalf with a
    value half the group contradicts.
  */
  const [addingTo, setAddingTo] = useState<{
    /*
      OPTIONAL from 06 Oct 2026, because a group is not always an item TYPE: the Real Property
      tab groups by CATEGORY, and that group has no type to seed with. A real-property form
      draws no item-type picker anyway — the server resolves the seeded row — so the absence
      is the honest value rather than a gap.
    */
    itemType?: { id: string; name: string };
    category?: { id: string; name: string };
    kind?: InventoryItemKind;
    unitCode?: string;
  } | null>(null);
  const [deletingType, setDeletingType] = useState<{ id: string; name: string; items: number } | null>(
    null,
  );
  /*
    The row kebab's Edit opens the ITEM's own form, so the record has to be fetched first: the
    level is a VIEW and carries a fraction of what the editor asks for. Held here rather than
    inside the menu, because a menu that closes on click cannot own the state of what it opened.
  */
  const [editing, setEditing] = useState<InventoryItemDetail | null>(null);

  /*
    The row an adjustment is being recorded against. The LEVEL itself, not a fetched item: the
    count form states the sku, the name, the category and what is on hand, all of which the
    row already carries — and it fetches the item separately anyway, for the expiry flag and the
    reference price the level does not send.
  */
  const [adjusting, setAdjusting] = useState<StockAdjustmentTarget | null>(null);

  /*
    The SERIALIZED half of the same job. Two states rather than one union, because the two
    forms take different targets and are mounted independently — a single `addingStock` would
    make every read of it ask which kind it is holding, on a page that already knows from the
    row it opened.
  */
  const [registering, setRegistering] = useState<StockUnitsRegisterTarget | null>(null);

  /*
    THE LICENCE REGISTRATION, and it is a SECOND state rather than a flag on the one above.

    The two forms take different targets and ask different questions — a licence carries no
    facility per row and no tenure at all — so one state holding either would make every
    consumer test which it had before reading a field. They are mutually exclusive by
    construction: `kind` decides which is opened, so no row can set both.
  */
  const [registeringLicences, setRegisteringLicences] = useState<LicenseRegisterTarget | null>(
    null,
  );

  /*
    The Issue form, and what the row it was opened from had already answered. `null` is closed;
    a target holding two nulls is the ISS chip, which is over the whole list and seeds nothing.

    <b>An OBJECT rather than the `'' | id` sentinel this held</b>, because the units tab's own
    kebab answers one question more: a reader who pressed Issue on a serial has named the UNIT
    as well as the item, and re-picking it out of a list of forty is what the seed removes.
  */
  const [issuing, setIssuing] = useState<{
    itemId: string | null;
    unitId: string | null;
    /*
      THE KIND, so the form can narrow its METHOD picker — a consumable is dispensed and an
      asset is assigned or lent, which the server has always enforced and the form did not
      read. NULL where the reader chose no row, and every method is then offered.
    */
    kind: InventoryItemKind | null;
  } | null>(null);

  /*
    THE UNITS TAB'S HAND-OFFS — a return against the slip a unit is out on, and a condition
    change on the unit itself.

    <b>One modal, never stacked</b>: each of these CLOSES the stock detail panel and opens in
    its place. `resume` holds the level the panel was showing so it can be put back on its
    stock tab once the action is done, which is what makes the round trip a hand-off rather
    than a dead end.
  */
  const [returning, setReturning] = useState<IssuanceDetail | null>(null);
  const [settingCondition, setSettingCondition] = useState<StockUnitListItem | null>(null);
  const [resume, setResume] = useState<{
    level: StockLevelListItem;
    agingKnown: boolean;
  } | null>(null);

  /*
    A hand-off that had to FETCH before it could open, and failed.

    Its own state rather than the page's `error`, which belongs to the delete dialog and is
    only ever drawn inside it. A failure the reader cannot see is the one thing a hand-off must
    not produce, because the panel it was launched from has already closed underneath them.
  */
  const [handoffError, setHandoffError] = useState<ApiError | null>(null);

  /*
    THE STOCK DETAIL PANEL, and which tab it opens on — both the row anchor and the kebab's
    Stock detail lead here, and the tab is what tells them apart: the anchor asks "what is this
    item", the kebab asks "what is actually held".

    The LEVEL ROW itself, not a fetched record: the panel's first tab is built entirely from
    what the row already carries, which is what makes opening it free and what keeps it from
    disagreeing with the table it opened from.
  */
  const [detail, setDetail] = useState<{
    level: StockLevelListItem;
    tab: StockDetailTab;
    /*
      WHETHER THE ROW'S TEN AGING FIGURES ARE REAL. The table says so when it hands the row
      over — see `<StockLevelsIndexTable onRowOpen>`. False means the aging call had not landed
      when the reader clicked, so the panel draws those rows as skeletons rather than printing
      the zeros `Project` left in them.
    */
    agingKnown: boolean;
  } | null>(null);

  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  /*
    WHICH item TYPES a write moved, so the reload behind it is scoped to those groups.

    Held rather than derived because by the time the answer comes back the modal that knew is
    closed. It is not cleared: a group reads it only when the reload counter moves, so a stale
    list is inert between writes.

    <b>The TYPE rather than the ITEM, and progressive loading is what forced that.</b> A group
    is what holds figures now, and its children are fetched only on expand — so naming the item
    would leave the table resolving it against rows it may never have asked for.

    <b>A LIST rather than one id, and the ISSUANCE forced that.</b> One slip carries lines across
    several types, so a scope naming one of them leaves every other group it touched showing
    figures that have just moved — stale under a correct row, with nothing on screen saying which
    is which. The two issuance forms therefore REPORT what they moved rather than being guessed at.
  */
  const [refreshingTypeIds, setRefreshingTypeIds] = useState<readonly string[] | null>(null);

  /*
    The TYPE a form about to open will move, captured where the reader chose it.

    A row’s kebab and the stock detail panel both know which level they were opened from; the
    modal that saves does not, because its target carries an item rather than a type. So the
    opener records it and the save reads it back — which is what lets one group skeleton while
    every other row on screen keeps its figures, its children and its open state.
  */
  const [movedTypeId, setMovedTypeId] = useState<string | null>(null);

  /*
    WHICH AXIS THE TABLE IS GROUPING ON, as the row kebabs read it.

    `levelQuery` derives it from the question — see `groupingAxis` — and writes it here as it
    builds each scope. A REF rather than state because nothing renders it: it is read by the
    handlers below to say which GROUP a write moved, and re-rendering the page to change the
    answer to a question nobody has asked yet would be a render per filter change.

    It is declared here rather than beside `scoped` because `groupIdOf` is closed over by
    handlers defined above that state, and a const read in a dependency array before its own
    declaration is a ReferenceError rather than a type error.
  */
  const groupsByCategory = useRef(false);

  /**
   * The GROUP a row sits in, on whichever axis the table is currently grouping by.
   *
   * It is what `movedTypeId` holds, so a write reloads the one group the row is actually drawn
   * under. Reading the item type unconditionally would name a group that is not on screen on
   * the Real Property tab, and nothing would reload — a scope that is quietly stale, which the
   * refresh rule calls worse than an unscoped one.
   */
  const groupIdOf = useCallback(
    (level: { inventoryItemTypeId: string; inventoryCategoryId: string }) =>
      groupsByCategory.current ? level.inventoryCategoryId : level.inventoryItemTypeId,
    [],
  );

  /*
    A WRITE LANDED. The groups refetch the query they are already showing, and the groups named
    here refetch their totals and their children — so the tab, the scroll, every other group’s
    figures and every other group’s open state all survive.

    A bare call names nothing and reloads every group, which is the honest treatment for a write
    whose reach the caller cannot state: retiring a type changes the group LIST, and registering
    an item may open a group that is not on screen yet.
  */
  const refresh = useCallback((typeIds: readonly string[] | null = null) => {
    setRefreshingTypeIds(typeIds);
    setReloadKey((k) => k + 1);
  }, []);

  /*
    A FORM OPENED AGAINST ONE ROW has just saved, so the group that row sits in is the whole of
    what moved.

    `movedTypeId` is null where the opener could not name one — a hand-off from a freshly
    registered item, whose group may not be on screen at all — and a reload naming nothing is the
    honest answer there rather than a scope pointing at whatever was opened last.
  */
  const refreshMoved = useCallback(() => {
    refresh(movedTypeId === null ? null : [movedTypeId]);
  }, [refresh, movedTypeId]);

  /**
   * The stock detail panel steps aside so an action can open — one modal, never stacked.
   *
   * The level it was showing is held rather than discarded, so a reader who changes their mind
   * is put back where they were instead of on a table they have to find their row in again.
   */
  /**
   * Opens the panel on its Item Info tab.
   *
   * <b>`agingKnown` is the DEDICATED table's to answer, and the shell cannot.</b> That table
   * holds the aging map per group and merges it onto the row it hands over, so it says whether
   * the ten clock-driven figures on that row are real. `<IndexPage>`'s own `onRowOpen` carries
   * one argument and its rows come from `/groups`, which has no figures at all — so the shell's
   * path passes false and the panel draws those rows as skeletons rather than printing zeros.
   */
  const openDetail = useCallback((level: StockLevelListItem, agingKnown: boolean) => {
    setDetail({ level, tab: 'info', agingKnown });
  }, []);

  /**
   * A FIGURE on the row opens the panel on its STOCK tab.
   *
   * <b>`agingKnown` is false here, and that is the honest answer rather than a
   * shortcut.</b> The table reports it beside the ROW when it hands one over; a column's
   * `render` is given the level and nothing else, so this handler cannot know whether the
   * ten clock-driven figures on it have landed. False makes the panel skeleton those rows
   * rather than print the zeros `Project` leaves behind — the same choice the shell's own
   * path takes, for the same reason.
   *
   * It costs nothing on the tab it opens: Batches, Units and Movements all fetch for
   * themselves.
   */
  const openStockDetail = useCallback((level: StockLevelListItem) => {
    setDetail({ level, tab: 'stock', agingKnown: false });
  }, []);

  const handOff = useCallback((level: StockLevelListItem, agingKnown: boolean) => {
    // The form about to open will move THIS type, and the modal that saves cannot say so.
    setMovedTypeId(groupIdOf(level));
    setHandoffError(null);
    setResume({ level, agingKnown });
    setDetail(null);
  }, [groupIdOf]);

  /**
   * Back to the panel, on its stock tab — which is the tab the reader was on when they left.
   *
   * <b>The CANCEL path only.</b> See `dropResume`.
   */
  const reopenDetail = useCallback(() => {
    if (resume !== null) setDetail({ ...resume, tab: 'stock' });
    setResume(null);
  }, [resume]);

  /**
   * After a WRITE the panel does not come back, and that is deliberate.
   *
   * Its first tab is built entirely from the level ROW the page is holding, and an issue, a
   * return or a condition change has just moved every figure on it — so reopening would put
   * the pre-change snapshot back in front of the reader, which is exactly the stale answer that
   * tab exists to avoid by reading the row rather than fetching a second one. The refreshed
   * table is where the effect is visible.
   */
  const dropResume = useCallback(() => setResume(null), []);

  /**
   * Open the return form for the slip a unit is out on.
   *
   * <b>A return is recorded against the SLIP</b>, and the unit row carries only its id and its
   * number — the form needs the LINES, so the whole record is fetched first. That is the shape
   * the issuances list's own Record return already takes.
   *
   * The panel is held open until the slip arrives, so a failure does not close it and leave the
   * reader on a table with nothing said. It closes on the failure too, because the sentence is
   * above the table and a dialog over the panel would be the stacked modal this product refuses.
   */
  const openReturn = useCallback(
    async (level: StockLevelListItem, unit: StockUnitListItem, agingKnown: boolean) => {
      if (unit.openIssuanceId === null) return;

      setHandoffError(null);

      try {
        const issuance = await issuancesApi.get(unit.openIssuanceId);

        handOff(level, agingKnown);
        setReturning(issuance);
      } catch (caught) {
        setDetail(null);
        setResume(null);
        setHandoffError(
          caught instanceof ApiError
            ? caught
            : new ApiError('Could not open the issuance this unit is out on.', 0),
        );
      }
    },
    [handOff],
  );

  /**
   * The CHILD row's kebab — captured from the hospital's item menu on 19 Sep 2026.
   *
   * That menu reads: Other Info · Pin to top · Issue · Restock · Record Opening Balance ·
   * Purchase Request · Stock Detail · Edit. **FOUR of the eight are built, and the other four
   * are ABSENT rather than drawn disabled** — the standing hide-don't-disable rule rather than
   * the chip row's exception one band above.
   *
   * The chips earn that exception because they are a standing strip stating the module's
   * roadmap once, at a glance. A per-row menu is the opposite shape: a dead entry is an
   * explanation the reader cannot act on, repeated on every item.
   *
   * **Issue was the fourth, on 20 Sep 2026.** It is in the kebab AND on the chip row, and that
   * is not one action in two places: the chip is over the whole list and opens the form with
   * nothing chosen, where the kebab is over a row and SEEDS the item. Same relationship the
   * anchor and the Create button already have.
   *
   * What is absent, and why, so nobody reads the gap as an oversight:
   *
   * - **Other Info** — permanently, not pending. `otherInfo` returns null on a derived row:
   *   no creator, no creation instant, no trail, so the entry would open a popover of four em
   *   dashes. That is a product call with its own row in the deviations table, and it is the
   *   one absence here that will never be filled.
   * - **Pin to top** — no pin table for inventory. `RepositoryPin` is the shape it would take.
   * - **Purchase Request** — procurement is unbuilt, and is already named on the PR chip.
   * - **Record Opening Balance** — and this one is absent on a PRODUCT call rather than a
   *   missing form. The reference keeps it beside its restock because that one ADDS and its
   *   opening balance ASSIGNS; here the assigning half is `<StockBatchModal>`'s correction,
   *   which writes an Adjustment saying the figure was wrong. Two ways to set a number, one of
   *   which leaves no line saying why, is the drift `StockLedger` exists to prevent.
   *
   * Each entry that IS here carries the key of what it DOES, never this page's own.
   */
  const rowActions = useCallback(
    (level: StockLevelListItem, agingKnown: boolean): MenuItem[] => [
      level.kind === ITEM_KIND.realProperty
        ? {
            /*
              THE REGISTER — what stock detail is for every other kind. A property type holds no
              lots and no units; what sits under it is the registered properties themselves, and
              the register is the page that lists instances.

              Same navigation shape as Stock detail below, and the same shortfall: it lands on
              the list rather than on this type's slice of it, because neither index reads a
              filter from the URL.
            */
            key: 'property-register',
            label: 'Property register',
            onClick: () => navigate('/inventory/settings/real-properties'),
            permission: 'real-properties.read',
          }
        : {
            /*
              STOCK DETAIL — the lots, or the individual units where the item is serialized.
              `tracksSerial` rides on the row for exactly this kind of decision: for a serialized
              item a quantity is a count of units, and for a quantity item it is a sum of lots.

              It lands on the LIST rather than on this item's slice of it, because neither index
              reads a filter from the URL. That is the same shortfall the UNT chip already has, by
              the same navigation — so the two behave alike rather than one promising more.
            */
            key: 'stock-detail',
            label: 'Stock detail',
            onClick: () => setDetail({ level, tab: 'stock', agingKnown }),
            permission: level.tracksSerial ? 'stock-units.read' : 'stock-batches.read',
          },
      /*
        THERE IS NO ISSUE ENTRY HERE — product call, 29 Sep 2026, and it REVERSES the call of
        20 Sep that put one in. **The one way to issue stock is New Issuance**, on the Create
        button’s shortcut menu.

        The entry was argued for as not-one-action-in-two-places: the menu is over the whole
        list and opens the form with nothing chosen, where the kebab was over a row and SEEDED
        the item. That reasoning is overruled rather than refuted — a reader looking for how to
        issue stock must find ONE control, and a second door to the same write is what this
        product refuses everywhere else.

        The SEED is what is given up, and it is stated rather than discovered: a reader who
        pressed Issue on a short item now re-answers which item in the form’s own picker.
      */
      /*
        STOCK ADJUSTMENT — a physical count against this item, which either corrects the lot
        already holding that batch number or opens one. It carries `stock-batches.create`, the
        key of the row it can write, never this page's own read key.

        **Absent for a SERIALIZED item**, rather than disabled with a reason: its stock is named
        one row at a time, so there is no lot to count and the server refuses one outright. The
        entry is not withheld because of who is reading — it does not apply to that item at all,
        and a laptop's menu offering a lot correction would be an affordance that can never work.
        Registering one is `Stock units`, which `Stock detail` above already opens.

        **And absent for REAL PROPERTY, on the same rule read one kind further.** A building is
        registered rather than stocked, so there is no lot of it to count — and the server
        refuses one outright, which is what makes this an absence rather than a hidden control.
      */
      ...(level.tracksSerial || level.kind === ITEM_KIND.realProperty
        ? []
        : [
            {
              // No icon, because neither of its siblings has one — a menu where one row is
              // iconed and the next is not reads as unfinished.
              key: 'adjust',
              label: 'Stock adjustment',
              permission: 'stock-batches.create',
              onClick: () => {
                setMovedTypeId(groupIdOf(level));
                setAdjusting(level);
              },
            },
          ]),
      /*
        REGISTER STOCK UNITS — the serialized half of the entry above, and the two are mutually
        exclusive by construction: `tracksSerial` decides which of them a row carries, so no
        menu ever offers both and no item is left with neither.

        **This is what that entry's own comment said was missing.** It read "registering one is
        `Stock units`, which `Stock detail` above already opens" — which was true and was a
        worse answer than it looked: that navigation lands on the whole units list, and adding
        the twelve laptops that arrived meant twelve trips through a form that asks which item
        each one is of. This one is opened from the row, so the item is already chosen, and it
        registers the whole delivery in one transaction.

        It carries `stock-units.create` — the key of the rows it writes, never this page's own
        read key, and the same key the single-unit form carries because it is that privilege
        performed once or twelve times.

        **ABSENT for REAL PROPERTY**, on the rule the entry above already reads one kind
        further: a building holds no stock at all, so neither form applies to it and the server
        refuses both. An absence rather than a hidden control.
      */
      /*
        A DIGITAL ASSET takes the LICENCE form, not the one below it — product call,
        23 Sep 2026. The two write the same rows through the same endpoint under the same key;
        what differs is what they ASK, because a licence has no facility per seat, no tenure of
        its own (it is derived from the licensing model) and a billing schedule the serialized
        grid has no column for.

        The entry is named for what it opens, in the reader's own word: a subscription's stock
        IS licences, and "Register stock units" over a form titled "Register licences" would be
        two words for one act on one screen.
      */
      ...(level.kind === ITEM_KIND.subscription
        ? [
            {
              key: 'register-licences',
              label: 'Register licences',
              permission: 'stock-units.create',
              onClick: () => {
                setMovedTypeId(groupIdOf(level));
                setRegisteringLicences({
                  id: level.id,
                  sku: level.sku,
                  name: level.name,
                  inventoryCategoryName: level.inventoryCategoryName,
                  onHand: level.onHand,
                  licensingModel: level.licensingModel ?? null,
                });
              },
            },
          ]
        : []),
      ...(level.tracksSerial
        && level.kind !== ITEM_KIND.realProperty
        && level.kind !== ITEM_KIND.subscription
        ? [
            {
              key: 'register-units',
              /*
                LABELLED 'Stock adjustment' — product call, 02 Oct 2026, in the reader's own
                words: *in equipments and etc, rename register stock units to Stock adjustment
                as well*. One word for adding stock, whichever kind the row is.

                THE COST IS STATED RATHER THAN HIDDEN, because it is the one this product
                normally refuses. These are two different ACTS: the entry one kind along writes
                a signed `Adjustment` — the record was wrong — and this one writes a `Receipt`
                per unit, which says stock ARRIVED. The batch side is careful about exactly that
                distinction, so a reader who learns the word on Expendables and meets it on
                Equipment is told a delivery is a correction.

                It also leaves the MENU and the FORM saying different things: the modal this
                opens is still titled 'Register stock units', which is the pairing
                'Register licences' exists to keep. Moving the modal's title would spread the
                mislabel rather than fix it, so the entry alone was renamed.
              */
              label: 'Stock adjustment',
              permission: 'stock-units.create',
              onClick: () => {
                setMovedTypeId(groupIdOf(level));
                setRegistering(level);
              },
            },
          ]
        : []),
      {
        /*
          EDIT the ITEM, under the item's own key. A level has no record to edit — every figure
          on it is derived — so this opens the record the row is a view OF, which is the same
          thing its anchor opens.
        */
        key: 'edit',
        label: 'Edit item',
        permission: 'inventory-items.update',
        onClick: () => {
          void (async () => {
            try {
              setEditing(await inventoryItemsApi.get(level.id));
            } catch (caught) {
              setError(
                caught instanceof ApiError
                  ? caught
                  : new ApiError('That item could not be opened.', 0),
              );
            }
          })();
        },
      },
    ],
    [navigate, groupIdOf],
  );

  /*
    Registering an item needs a category, an item type and a unit — three REQUIRED pickers, so
    the shell states what is missing before the reader opens a form they could not submit.

    <b>And a STORAGE FACILITY, which is the fourth and is not one of those — 01 Oct 2026.</b>
    An item registers perfectly well without one, and then nothing can be done with it: a count
    is taken somewhere and every unit is kept somewhere, so the adjustment, the unit
    registration and the issuance are each refused for a reason nothing on this page states.
    The reader meets it as three dead row-kebab entries and a disabled tick on a form they have
    already filled in.

    So it is an ADVISORY prerequisite — named in the banner, and it does NOT disable Create.
    That is the standing product call honoured rather than reversed: blocking Create over a
    missing facility would stop registering items, which has nothing to do with stock, and it is
    the same reason the item form DISABLES its hand-off tick instead of withholding the form.

    Seeded MET and left met on a failed feed: flashing "set up categories first" on every load
    of a working module is worse than a banner arriving a moment late, and a request that did
    not come back is not evidence that a catalog is empty.
  */
  const [ready, setReady] = useState({
    categories: true,
    itemTypes: true,
    units: true,
    facilities: true,
  });

  useEffect(() => {
    const controller = new AbortController();

    Promise.all([
      inventoryCategoriesApi.options(controller.signal),
      inventoryItemTypesApi.options(controller.signal),
      unitsOfMeasureApi.options(controller.signal),
      storageFacilitiesApi.options(controller.signal),
    ])
      .then(([categories, itemTypes, units, facilities]) => {
        if (controller.signal.aborted) return;

        setReady({
          categories: categories.length > 0,
          itemTypes: itemTypes.length > 0,
          units: units.length > 0,
          facilities: facilities.length > 0,
        });
      })
      .catch(() => undefined);

    return () => controller.abort();

    // Re-asked after a write, so filling the last empty catalog clears the banner without a
    // reload — the reader fixed it, and the page should agree.
  }, [reloadKey]);

  const groupActions = useCallback(
    (group: { id: string; name: string; items: StockLevelListItem[] }): MenuItem[] => [
      {
        key: `add-${group.id}`,

        // REGISTER, not Add — the same word the form's title and the toolbar's button use.
        // One act, one word: a menu entry reading Add over a modal reading Register is two
        // names for the thing the reader is about to do.
        label: 'Register item',
        icon: <Plus className="h-4 w-4" aria-hidden="true" />,

        // The ITEM's create key, not the type's — this opens the item form.
        permission: 'inventory-items.create',
        onClick: () =>
          setAddingTo({
            /*
              THE GROUP SEEDS WHICHEVER AXIS IT IS. On a type group it is the item type; on a
              CATEGORY group it is the category, and there is no type to name — seeding
              `itemType` from a category id would file the new item under a type that does
              not exist.
            */
            itemType: groupsByCategory.current ? undefined : { id: group.id, name: group.name },

            // Only where the group's rows AGREE — see `addingTo`. `unanimous` returns
            // undefined on a disagreement, which leaves the field free rather than seeded
            // with a value half the group contradicts. A CATEGORY group agrees by
            // construction, so the group's own name is the answer and no row need be read.
            category: groupsByCategory.current
              ? { id: group.id, name: group.name }
              : unanimous(group.items, (level) => level.inventoryCategoryId)
              ? {
                  id: group.items[0].inventoryCategoryId,
                  name: group.items[0].inventoryCategoryName,
                }
              : undefined,
            kind: unanimous(group.items, (level) => level.kind) ? group.items[0].kind : undefined,

            /*
              SUGGESTED rather than locked, unlike the three above it. The type, the category
              and the kind are facts about the group; the unit is not — registering a case of
              something under a type counted in pieces is a real thing to do, so the form offers
              an answer and lets the reader overrule it.

              A CODE, because the level row carries no unit id. The form resolves it against the
              units it already loads, which costs no wire field.
            */
            unitCode: commonUnitCode(group.items),
          }),
      },
      /*
        DELETE TYPE IS ABSENT ON A CATEGORY GROUP, and that is the hide rule rather than an
        omission: the id in hand is a category's, so the entry would either delete the wrong
        row or refuse. A category is maintained under Settings, where its own key gates it.
      */
      ...(groupsByCategory.current
        ? []
        : [
            {
              key: `delete-type-${group.id}`,
              label: 'Delete type',
              icon: <Trash2 className="h-4 w-4" aria-hidden="true" />,
              permission: 'inventory-item-types.delete',
              destructive: true,
              onClick: () =>
                setDeletingType({ id: group.id, name: group.name, items: group.items.length }),
            } satisfies MenuItem,
          ]),
    ],
    [],
  );

  /*
    REBUILD — recompute every item's stored figures from the lots, the units, the issuance lines
    and the ledger.

    Eleven of the columns on this register are stored rather than derived on every read, written
    by whatever moves the stock. Nothing increments them, so a maintained row can only ever be
    STALE — never wrong — and this is what puts a stale one back in step. The reader reaches for
    it when a figure disagrees with the records it is drawn from.
  */
  const [rebuilding, setRebuilding] = useState(false);
  const [confirmRebuild, setConfirmRebuild] = useState(false);
  const [rebuilt, setRebuilt] = useState<number | null>(null);

  async function runRebuild() {
    setRebuilding(true);
    setError(null);

    try {
      const written = await stockLevelsApi.rebuild();

      setConfirmRebuild(false);
      setRebuilt(written);

      // Names no type, so every group reloads — the rebuild touched all of them.
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught
          : new ApiError('The stock figures could not be rebuilt.', 0),
      );
    } finally {
      setRebuilding(false);
    }
  }

  async function runDeleteType() {
    if (!deletingType) return;

    setDeleting(true);
    setError(null);

    try {
      await inventoryItemTypesApi.deactivate(deletingType.id);
      setDeletingType(null);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The type could not be deleted.', 0),
      );
    } finally {
      setDeleting(false);
    }
  }

  /**
   * The SHELL’s query as the one every stock-level call takes.
   *
   * All four answer different halves of ONE question, so they are serialised from one place:
   * a summary counted over a different filter from the groups beneath it would put a badge
   * over a tab whose own rows disagree with it.
   */
  const levelQuery = useCallback(
    (query: IndexQueryState): StockLevelListQuery => ({
      search: query.search,
      page: query.page,
      pageSize: query.pageSize,
      sort: query.sort ?? undefined,
      kind: query.facets.kind?.join(','),
      tenure: query.facets.tenure?.join(','),
      category: query.facets.category?.join(','),
      stock: query.facets.stock?.join(','),
      alert: query.facets.alert?.join(','),

      /*
        THE GROUPING AXIS, derived from the question rather than from the tab — so a reader who
        reaches a list of nothing but real property through the KIND facet gets the same
        grouping as one who pressed the tab. See `groupingAxis`.
      */
      groupBy: groupingAxis(query.facets.kind),
    }),
    [],
  );

  /*
    THE QUERY THE LIST IS CURRENTLY ANSWERING, so the three calls the shell does NOT make can
    be scoped to it.

    The shell owns the query and hands it to `fetch` alone; the summary, the per-group figures
    and the children are this page’s own calls and have nowhere else to read it from. Held with
    a KEY rather than compared field by field, so an identical query does not re-run three
    effects on every render.
  */
  const [scoped, setScoped] = useState<{ query: StockLevelListQuery; key: string } | null>(null);

  /*
    THE BADGES, THE TILES AND THE RAIL — counted on their own call and merged over the list’s.

    Step 0 of four, fired first and awaited by NOTHING. It is the most expensive of them —
    every axis counted against every other, two of them derived, measured at up to 4.5 s
    against SQL Server — where the groups beneath it come back in under 100 ms. Blocking the
    table on it would give back everything the split buys.

    <b>It fails SOFT.</b> A failed summary leaves the previous counts standing rather than
    emptying every badge on the page: a request that did not come back is not evidence that a
    tab holds nothing.
  */
  const [summaryFacets, setSummaryFacets] = useState<Record<string, FacetValue[]>>({});

  /*
    THE TAB BADGES, one entry per tab, each written as its own call lands.

    They are the FIRST thing asked for on this screen — product call, 28 Sep 2026 — because
    they answer the question a reader asks before any other: is anything waiting for me on a
    tab I am not looking at. They used to ride in on the summary, behind every other axis and
    behind the groups, which put the one number somebody reads first last in the queue.

    <b>A tab with no entry yet draws NO badge</b>, which is what the shell already does with a
    zero — so a tab is silent until it has something to say rather than showing a placeholder.
  */
  const [badges, setBadges] = useState<Record<string, number>>({});

  /*
    The last question the badges were asked, so switching TABS costs nothing.

    A badge is scoped to its own tab and excludes the two alert axes, so the only things that
    move it are the SEARCH and the CATEGORY — not which tab the reader is standing on. Keyed
    on the whole query it would re-fire five calls on every tab change for five identical
    answers.
  */
  const badgeKey = useRef<string | null>(null);
  const badgeRun = useRef<AbortController | null>(null);

  /**
   * Fire one count per tab. Called from `fetchLevels` BEFORE it issues its own request.
   *
   * <b>Nothing clears the previous numbers.</b> A badge one second stale is a smaller lie
   * than a row of tabs that briefly say nothing, and on a tab change the answers are
   * identical anyway — the same trade the profile nav’s counts already make.
   *
   * <b>It fails SOFT</b>, per call: a tab whose count did not come back keeps whatever it had
   * rather than dropping to nothing, because a request that failed is not evidence that a tab
   * holds no work.
   */
  const loadBadges = useCallback((scope: StockLevelListQuery, reload: number) => {
    const key = `${scope.search ?? ''}|${scope.category ?? ''}|${reload}`;

    if (badgeKey.current === key) return;
    badgeKey.current = key;

    badgeRun.current?.abort();

    const controller = new AbortController();
    badgeRun.current = controller;

    for (const slice of SLICES) {
      /*
        The TAB’s own two axes, replacing whatever the reader is standing on — every tab is
        counted, including the four they are not looking at, which is the whole point of a
        cross-tab badge.
      */
      const scoped: StockLevelListQuery = {
        ...scope,
        kind: slice.select.kind?.join(','),
        tenure: slice.select.tenure?.join(','),
      };

      stockLevelsApi
        .attention(scoped, controller.signal)
        .then((count) => {
          if (!controller.signal.aborted) {
            setBadges((current) => ({ ...current, [slice.key]: count }));
          }
        })
        .catch(() => {
          // Fails soft, per tab — see the remarks above.
        });
    }
  }, []);

  /**
   * Step 1 — the ITEM TYPES, which is what draws the table.
   *
   * The shell’s own fetch, so the pager, the empty state and the record count all page over
   * GROUPS. Each row arrives with a name and a count and no figures at all; the figures follow
   * per row, and the children follow on expand.
   */
  const fetchLevels = useCallback(
    async (query: IndexQueryState, signal: AbortSignal) => {
      const scope = levelQuery(query);
      const key = JSON.stringify(scope);

      // Written before anything is awaited, so a kebab opened on the first painted row already
      // names the group that row is drawn under.
      groupsByCategory.current = scope.groupBy === 'category';

      /*
        THE BADGES GO OUT FIRST — before this call issues its own request, not after it comes
        back. They are what a reader reads before they read anything else, and the ordering is
        the instruction rather than an optimisation.

        Called from here rather than from an effect for exactly that reason: an effect runs
        after the render that follows this resolving, which is the position they were in.
      */
      loadBadges(scope, reloadKey);

      /*
        And the SCOPE is published before the await, so the summary and every group’s figures
        start alongside the groups rather than queueing behind them.
      */
      setScoped((current) => (current?.key === key ? current : { query: scope, key }));

      const page = await stockLevelsApi.groups(scope, signal);

      return { ...page, items: page.items.map(pendingGroupRow) };
    },
    [levelQuery, loadBadges, reloadKey],
  );

  /*
    Step 0, on its own clock. Re-run when the question changes AND when a write lands, because
    an issuance moves the tab badges and the alert tiles exactly as it moves a group’s totals.
  */
  useEffect(() => {
    if (scoped === null) return;

    const controller = new AbortController();

    stockLevelsApi
      .summary(scoped.query, controller.signal)
      .then((facets) => {
        if (!controller.signal.aborted) setSummaryFacets(facets);
      })
      .catch(() => {
        // Fails soft on purpose — see the state’s own remarks.
      });

    return () => controller.abort();
  }, [scoped, reloadKey]);

  /**
   * Step 2 — ONE group’s summed figures. A 404 is a group the filters emptied, not an error.
   */
  const fetchFigures = useCallback(
    (typeId: string, signal: AbortSignal) =>
      scoped === null
        ? Promise.resolve(null)
        : stockLevelsApi.groupFigures(typeId, scoped.query, signal),
    [scoped],
  );

  /**
   * Step 3 — ONE group’s children, on expand. The only call that runs the whole projection.
   *
   * <b>Its own page size, and it is the platform’s CAP rather than a number chosen here</b> —
   * see `CHILDREN_PAGE_SIZE`. The shell’s paging is over GROUPS now, so a reader on page 1 of
   * ten types is not asking for the first twenty ITEMS, and a group’s children have no pager of
   * their own to reach a second page with.
   */
  const fetchChildren = useCallback(
    async (typeId: string, signal: AbortSignal) => {
      if (scoped === null) return [];

      const page = await stockLevelsApi.list(
        { ...scoped.query, group: typeId, page: 1, pageSize: CHILDREN_PAGE_SIZE },
        signal,
      );

      return page.items;
    },
    [scoped],
  );

  /**
   * The FIFTH call — the figures that age, for one group's children.
   *
   * <b>Separate so the rows appear first.</b> The children above are one join over the stored
   * figures and land immediately; expired, near expiry, delayed return, the two lease counts and
   * the stock status each cost a correlated subquery per row. Fetching them apart means the
   * table draws and those cells fill in, rather than the whole group waiting on the slowest half
   * of itself.
   *
   * <b>They cannot be stored, which is why this call exists at all.</b> Each is a date compared
   * against TODAY, so a stored copy would be correct only as of that item's last recompute — a
   * lot lapsing overnight on an item nobody touched would sit in Available with every figure
   * around it agreeing.
   */
  const fetchAging = useCallback(
    async (typeId: string, signal: AbortSignal) => {
      if (scoped === null) return [];

      return await stockLevelsApi.aging(
        { ...scoped.query, group: typeId, page: 1, pageSize: CHILDREN_PAGE_SIZE },
        signal,
      );
    },
    [scoped],
  );

  /*
    WHAT THE SHELL READS — the alert tiles and the rail from the summary, the tab badges from
    their own five calls, merged per axis.

    The two arrive independently and neither waits for the other, which is what lets the
    badges be first: `attention` is filled here and nowhere else, because the summary stopped
    counting it when the dedicated call took over.
  */
  const extraFacets = useMemo(
    () => ({
      ...summaryFacets,
      attention: SLICES.filter((slice) => badges[slice.key] !== undefined).map((slice) => ({
        value: slice.key,
        label: slice.label,
        count: badges[slice.key]!,
      })),
    }),
    [summaryFacets, badges],
  );

  /*
    The FILTERS a per-group fetch is scoped to. The reload counter travels beside it rather
    than inside it — see `<StockLevelsIndexTable reloadKey>`, where the two are kept apart
    because only one of them may ever be ignored.
  */
  const queryKey = scoped?.key ?? '';

  /*
    A SEARCH OPENS THE GROUPS — product call, 02 Oct 2026, in the reader's own words: *when
    using search, and there is a result, expand that group automatically*.

    The groups call is already scoped to the term, so every row that comes back holds a match
    and there is nothing left for the reader to choose between at the group level. Leaving them
    shut makes them click once more to reach the thing they just searched for.

    It reads the SCOPED query rather than the raw input, so the groups open when the ANSWER
    arrives rather than on the keystroke — the list and the folders move together.
  */
  const expandGroups = (scoped?.query.search ?? '').trim().length > 0;

  return (
    <>
    <IndexPage<StockLevelListItem>
      module="stock-levels"
      /*
        NO BREADCRUMB. Product call, 22 Sep 2026, and it is the only index page in the product
        without one.

        This page is reached only from the sidebar, which already states where it sits — and it
        carries more chrome above its table than any other: a tab strip naming the kind, eight
        alert tiles and a row of shortcut chips. A fourth line saying `Logistics › Inventory ›
        Stock Levels` on top of that is a second answer to a question the tab strip answers more
        loudly, and it costs the vertical space the tiles need.

        <b>A page reached THROUGH another one may not do this</b>, and the distinction is not
        cosmetic: the breadcrumb is the back-nav, so a module whose entry point is a settings
        menu would become reachable and not leavable. See the prop's own remarks.
      */
      columns={COLUMNS}
      /*
        KIND is the folder-nav axis, which is the source system's own tab row — Consumables,
        Equipment, Digital & Subscriptions. The READER can still repoint it from the rail's cog
        like any other page; this is only the default.
      */
      /*
        The reference's own taxonomy — see `SLICES`. Not the kind enum: two of its tabs are a
        kind PLUS a tenure, which a facet-value strip cannot express.
      */
      /*
        The bands, offered in the cog's Headers section. Twenty-five columns is more than most
        readers use at once, and every group put away is width the rest of the row gets back.

        Four of the ten hold ONE column and draw no band — see `BANDS`. A column in no group can
        never be hidden, so Investment, Near expiry, Latest movement and Stock status each get a
        group of their own rather than a band nobody asked for.
      */
      headerGroups={BANDS}
      navSlices={SLICES}
      /*
        TABLE, which is the one place in the product a page overrules the list default. A list
        row carries a title, a badge and three summary values; this page is twenty figures
        under four banded headings, so opening as a list would answer none of the questions it
        exists for. The reader's own choice still wins and is still remembered.
      */
      defaultView="table"
      /*
        The tabs are this page's PRIMARY filing — the item kinds — and the search, the tiles
        and the chips all read the kind in front of the reader, so they are filed under it
        rather than spanning the page above it.
      */
      chromeInPanel
      /*
        Moving between kinds should not blank the table and refetch a page the reader saw ten
        seconds ago. Keyed on the whole query, cleared by any write, never persisted.
      */
      cacheAnsweredQueries
      /*
        The DEDICATED table — see `<StockLevelsIndexTable>`. Everything around it is still the
        shell's; only the table surface is this module's own, because its rows group under a
        summed parent and its anchor is a stacked cell.
      */
      /*
        A FIGURE OPENS THE STOCK DETAIL PANEL — product call, 02 Oct 2026.

        On its STOCK tab rather than Item Info, which is the one decision in this: the reader
        pressed a QUANTITY, and what makes that quantity up is the lots or the units. Item
        Info answers what the item IS, which is a different question and is what the row
        ANCHOR is for — so the two destinations stay distinct rather than the figure becoming
        a second door to the same tab.
      */
      renderTable={(surface) => (
        <StockFigureContext.Provider value={openStockDetail}>
        <StockLevelsIndexTable
          {...surface}
          refreshingTypeIds={refreshingTypeIds}
          queryKey={queryKey}
          expandGroups={expandGroups}
          reloadKey={reloadKey}
          fetchFigures={fetchFigures}
          fetchChildren={fetchChildren}
          fetchAging={fetchAging}
          agingColumns={AGING_COLUMN_KEYS}
          bands={BANDS}
          groupActions={groupActions}
          rowActions={rowActions}
          /*
            AFTER the spread, so the DEDICATED table's two-argument form wins over the shell's.
            It is the only surface that knows whether a row's aging figures have landed.
          */
          onRowOpen={openDetail}
        />
        </StockFigureContext.Provider>
      )}
      /*
        The chips and the alert tiles, in the shell's own slot above the folder tabs. The tiles
        take the facet state rather than holding their own, so they and the rail's
        Needs-attention group cannot disagree about what is ticked.
      */
      cardGroups={ALERT_CARDS}
      aboveTable={({ hiddenCards, counts, selected, selectFacet, loading, activeSlice }) => (
        <div className="flex flex-col gap-3">
          {/*
            A hand-off that could not open. It sits here rather than in a dialog because the
            panel it was launched from has already closed — the reader is looking at the table,
            which is where the sentence has to be.
          */}
          {handoffError ? (
            <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
              <p className="text-xs text-red-700">{handoffError.message}</p>
            </div>
          ) : null}

          {/*
            WHAT A REBUILD DID, stated where the reader is standing rather than in a second
            dialog.

            It has to say something: on a register whose figures were already correct nothing
            visibly changes, so a rebuild that reported nothing is indistinguishable from a
            click that missed. A receipt modal was written first and taken out — `<ConfirmDialog>`
            owns the word on its button, and a dialog whose button is never a confirmation is the
            wrong component rather than a case for overriding it.

            It clears itself on the next rebuild and on a failure, so it can never sit over a
            figure it is no longer describing.
          */}
          {rebuilt !== null ? (
            <div role="status" className="rounded-lg border border-slate-200 bg-slate-50 p-3">
              <p className="text-xs text-slate-600">
                Rebuilt the stored figures for {rebuilt} {rebuilt === 1 ? 'item' : 'items'} from
                their lots, units, issuances and ledger.
              </p>
            </div>
          ) : null}

          {/*
            TWO axes, because the tiles genuinely span two. Out of stock and Low are values of
            the STOCK status — one derived state per item — where the other six are
            needs-attention flags that co-occur. They read `alert` and emitted neither, so both
            read zero forever and pressing one emptied the table; pointing them at the facet
            that already counts and already filters is the fix that adds nothing.

            The SLICE goes in so the strip can draw only what the tab's kind can report — a
            Lease Ending tile over a list of consumables can only ever read zero.
          */}
          <StockLevelAlerts
            alertCounts={Object.fromEntries(
              (counts.alert ?? []).map((value) => [value.value, value.count]),
            )}
            stockCounts={Object.fromEntries(
              (counts.stock ?? []).map((value) => [value.value, value.count]),
            )}
            alertSelected={selected.alert ?? []}
            stockSelected={selected.stock ?? []}
            sliceKey={activeSlice?.key}
            hidden={hiddenCards}
            loading={loading}
            onSelect={selectFacet}
          />
        </div>
      )}
      /*
        THE SHORTCUTS, on the Create button's trailing edge rather than in a strip of their own.

        Two nulls on the Issue hand-off: the menu is over the whole list, so it seeds nothing —
        which is what separates it from the row kebab's Issue, where the reader has already named
        the item by choosing whose menu to open.
      */
      shortcutMenu={stockLevelShortcutItems({
        canReadUnits,
        canIssue,
        onIssue: () => {
          /*
            NO ROW, so no seed — and no SCOPE either. The form reports the types it moved on
            save, but `movedTypeId` would otherwise still hold whatever row kebab was opened
            last, and the cancel path reads it. Cleared so a scope can only ever be one this
            opening actually named.
          */
          setMovedTypeId(null);
          setIssuing({ itemId: null, unitId: null, kind: null });
        },
        navigate,
      })}
      /*
        Named for what is BEHIND it rather than "Shortcuts", which the shell would default to: a
        chevron is icon-only, so this is its accessible name and its tooltip both.
      */
      shortcutLabel="Inventory shortcuts"
      facets={[
        /*
          NO `kind` group — the tab strip owns that axis, and the shell drops any axis a slice
          names from the rail. Two controls over it could not agree: the rail's `All` row
          clears the axis, and a slice taxonomy has no All tab to show for it.
        */
        { key: 'stock', label: 'Stock status' },
        { key: 'alert', label: 'Needs attention' },
        { key: 'category', label: 'Category' },
      ]}
      rowKey={(level) => level.id}
      fetch={fetchLevels}
      /*
        The counts arrive on their OWN call, behind a table the reader is already reading — see
        `summaryFacets`. Merged per axis over whatever the groups call returned, which is
        nothing: `GroupsAsync` sends no facets at all, because computing them there is exactly
        the cost this split exists to move off the first paint.
      */
      extraFacets={extraFacets}
      reloadKey={reloadKey}
      /*
        REGISTER AN ITEM, from the page that shows what is short of stock. The reader spotting a
        gap should not have to navigate two levels into Settings to fill it — which is a cost the
        Items move introduced, and this is what pays it back.

        The key is the ITEM's, not this page's: it is the key of what the action DOES. The label
        names the noun for the same reason — a bare "Create" here reads as creating a level,
        which is not a thing that can be done.
      */
      onCreate={(slice) => setCreating({ kind: slice ? SLICE_KIND[slice.key] : undefined })}
      createPermission="inventory-items.create"
      /*
        REGISTER, not Create — one word for one concept, and it is the word the form's own title
        uses. A bare "Create" here would read as creating a LEVEL, which is not a thing that can
        be done; "Register item" names what the button actually opens.
      */
      createLabel="Register item"
      prerequisites={[
        {
          label: 'categories',
          met: ready.categories,
          to: '/inventory/settings/categories',
          permission: 'inventory-categories.read',
        },
        {
          label: 'item types',
          met: ready.itemTypes,
          to: '/inventory/settings/item-types',
          permission: 'inventory-item-types.read',
        },
        {
          label: 'units of measure',
          met: ready.units,
          to: '/inventory/settings/units',
          permission: 'units-of-measure.read',
        },
        {
          label: 'storage facilities',
          met: ready.facilities,
          to: '/inventory/settings/storage-facilities',
          permission: 'storage-facilities.read',

          /*
            ADVISORY — see the probe above. The sentence is the page’s because only this page
            knows what a facility is for here, and it names the three acts rather than saying
            "stock functions", which is a phrase no screen in this product uses.

            <b>It must not say what Create can still do.</b> It read "Items can still be
            registered, but …", which at the time landed directly after "so the form cannot be
            completed" — a flat contradiction on one banner. Found by rendering the five states
            rather than by reading it.

            The APPEND is gone as of 06 Oct 2026: the banner now names ONE requirement at a
            time, so this sentence leads only once nothing blocking is missing and never sits
            beside the other. <b>The constraint stands anyway</b> — an advisory entry states
            what is LOST without the catalog, which is the thing the reader cannot see; what
            still works is already in front of them.
          */
          advisory:
            'No stock can be recorded until a store exists — a count, a unit registration and an issuance each have to name one.',
        },
      ]}
      searchPlaceholder="Search SKU, name, brand or model"
      emptyMessage="No items yet. Stock levels are derived from the item catalog."
      /*
        The anchor opens the ITEM, because that is the record behind the row — a level has no
        profile of its own and must not pretend to. It carries the ITEM's read key for the same
        reason: it is a link into another module, gated on what it opens rather than on the page
        it sits on.
      */
      /*
        THE STOCK DETAIL PANEL — what is held of this item, without leaving the list. Product
        call, 20 Sep 2026, and it replaces a navigation to the item's CATALOG profile, which
        answers what the item IS rather than what is held of it.

        NO `openPermission`. Every value the panel's first tab shows is already on the row the
        reader is looking at, so there is no second key to hold — and the two collection tabs
        carry the keys of the modules they read, which `<FolderTabs>` hides them on.
      */
      onRowOpen={(level) => openDetail(level, false)}
      /*
        NO audit columns. This row is not a record — it has no creator, no creation instant and
        no update trail, and the shell would otherwise append three columns of em dashes to a
        table that is already thirty wide.
      */
      auditColumns={false}
      /*
        The three entity types every figure here is DERIVED from, which is exactly what a
        module history is for — "why did this fall from 40 to 12" is answered by the ledger.
        The module writes none of them itself, and that is not a reason to leave the scope
        empty: an empty list is "ticked to nothing" on the wire and matches no row, so the
        entry would open onto a modal that can never show anything.
      */
      auditEntityTypes={['StockBatch', 'StockUnit', 'StockMovement']}
      /*
        REBUILD, in the cog beside History Logs rather than among the view switches above it:
        it DOES something rather than changing what the list is showing, which is the line
        `recordActions` draws against `recordFilters`.

        It carries its own key — a reader of the register must not be able to fire a statement
        that rewrites every item in the system — and is hidden without it, like every other
        gated entry in this menu.
      */
      recordActions={[
        {
          key: 'rebuild',
          label: 'Rebuild stock figures',
          permission: 'stock-levels.rebuild',
          onClick: () => {
            setRebuilt(null);
            setError(null);
            setConfirmRebuild(true);
          },
        },
      ]}
      /*
        NULL, which drops Other Info from the row menu — and with no verb to put beside it the
        menu is empty, so no kebab is drawn at all. A popover of four em dashes would be the
        dead affordance the hide-rule forbids, and it is the honest answer here: there is no
        creator and no update trail to state, because nothing on this row was ever written.
      */
      otherInfo={() => null}
    />

    {/*
      ADD ITEM, opened from a group that already names its type — so the type is seeded and
      locked, exactly as the org chart locks the parent on a child added from a node's menu.
      The reader chose it by choosing whose menu to open.
    */}
{creating || addingTo || editing ? (
      <InventoryItemModal
        open
        editing={editing}
        // Seeded and LOCKED only when opened from a group or a tab; the toolbar's Create on an
        // unsliced page asks freely.
        initialItemType={addingTo?.itemType}
        initialCategory={addingTo?.category}
        initialUnitCode={addingTo?.unitCode}
        initialKind={addingTo ? addingTo.kind : creating?.kind}
        onClose={() => {
          setCreating(null);
          setAddingTo(null);
          setEditing(null);
        }}
        onSaved={() => {
          setCreating(null);
          setAddingTo(null);
          setEditing(null);
          refresh();
        }}
        /*
          STRAIGHT INTO ADDING STOCK, when the reader ticked for it. `onHand` is 0 because it
          is: the item exists and no stock has been recorded against it, which is what both
          forms state as a read-only fact beside the item.

          **WHICH form is `tracksSerial`'s to decide, and the modal reads it off the record the
          form just created** rather than off anything this page is holding — a level row for a
          freshly registered item does not exist yet, which is the whole reason both targets are
          a SHAPE rather than a `StockLevelListItem`.

          Supplying this handler is also what DRAWS the checkbox — the absent-callback idiom —
          so a page that can open neither form never offers one.
        */
        onAdjust={(item) => {
          /*
            THE GROUP THE NEW ITEM LANDS IN, named before either form opens.

            Without it the three saves below scoped their reload to whatever row kebab was opened
            last — refreshing a group nothing had happened to, and leaving the newly stocked one
            showing the zeros it was registered with. The type is on the record the form has just
            created, which is the only place it exists at this point.

            A group that is not on screen YET is handled by the reload counter rather than by
            this: every refresh refetches the group LIST, so a first-of-its-type item brings its
            group with it and that group mounts already fetching.
          */
          setMovedTypeId(groupIdOf(item));

          /*
            THREE forms now, and the KIND decides between them before `tracksSerial` does — a
            digital asset is serialized by force, so testing the flag first would send every
            licence to the physical-stock grid it was given a form of its own to replace.

            `onHand` is 0 because it is: the item exists and nothing has been recorded against
            it, which is what all three state as a read-only fact beside the item.
          */
          if (item.kind === ITEM_KIND.subscription) {
            setRegisteringLicences({
              id: item.id,
              sku: item.sku,
              name: item.name,
              inventoryCategoryName: item.inventoryCategoryName,
              onHand: 0,
              licensingModel: item.licensingModel,
            });

            return;
          }

          const target = {
            id: item.id,
            sku: item.sku,
            name: item.name,
            unitCode: item.unitCode,
            inventoryCategoryName: item.inventoryCategoryName,
            onHand: 0,

            /* The registration withholds its subscription-only Term ends column on anything
               else, so it has to be told what the item is. The count form ignores it. */
            kind: item.kind,
          };

          if (item.tracksSerial) {
            setRegistering(target);
          } else {
            setAdjusting(target);
          }
        }}
      />
    ) : null}

    {/*
      THE STOCK DETAIL PANEL. Mounted only while a row is open, so its per-tab fetch and its
      drop-on-item-change effect never run for a closed modal.
    */}
    {detail ? (
      <StockDetailModal
        open
        level={detail.level}
        agingKnown={detail.agingKnown}
        initialTab={detail.tab}
        onClose={() => setDetail(null)}
        /*
          THE UNITS TAB'S ROW MENU, wired. Each entry carries the key of what it DOES and the
          panel decides which of them apply to a given unit; an absent callback removes its
          entry outright, which is what keeps a surface that cannot perform one from offering
          it. Attach receipt is deliberately absent everywhere — there is no table behind it.
        */
        onIssue={(unit) => {
          handOff(detail.level, detail.agingKnown);
          setIssuing({ itemId: unit.inventoryItemId, unitId: unit.id, kind: detail.level.kind });
        }}
        onReturn={(unit) => void openReturn(detail.level, unit, detail.agingKnown)}
        onSetCondition={(unit) => {
          handOff(detail.level, detail.agingKnown);
          setSettingCondition(unit);
        }}
        onOpenUnit={(unit) => {
          // A navigation, so there is nothing to come back to — the panel closes and stays shut.
          setDetail(null);
          navigate(`/inventory/stock-units/${unit.id}`);
        }}
      />
    ) : null}

    {/*
      THE ISSUE FORM. Mounted only while it is open, so every state inside it resets between
      releases — and a save refreshes the list, because the figures on every row this issuance
      touched have just moved.
    */}
    {issuing !== null ? (
      <IssuanceModal
        open
        initialItemId={issuing.itemId}
        initialUnitId={issuing.unitId}
        initialKind={issuing.kind}
        onClose={() => {
          setIssuing(null);
          reopenDetail();
        }}
        /*
          THE FORM SAYS WHICH GROUPS IT MOVED, one per line, rather than the page guessing at the
          row it was opened from — a slip may carry lines the reader added beside it, and a scope
          naming only the seed leaves those groups stale.
        */
        onSaved={(movedTypeIds) => {
          setIssuing(null);
          dropResume();
          refresh(movedTypeIds);
        }}
      />
    ) : null}

    {/*
      RECORD A RETURN, against the SLIP the unit is out on rather than against the unit — which
      is where a return is recorded in this product, and why the entry states the slip number.
    */}
    {returning ? (
      <RecordReturnModal
        open
        issuance={returning}
        onClose={() => {
          setReturning(null);
          reopenDetail();
        }}
        /*
          ONE LINE, so one group — and the FORM names it, because the slip this return is against
          may hold lines for items this page's row never mentioned.

          It reloaded every group until 29 Sep 2026, which is the half of the scoped reload that
          was never wired: a return moves exactly what an issuance moves, in the other direction.
        */
        onSaved={(movedTypeIds) => {
          setReturning(null);
          dropResume();
          refresh(movedTypeIds);
        }}
      />
    ) : null}

    {/*
      SET CONDITION — in stock, damaged or under repair. Taking a unit OFF the tally is the
      write-off, which is a different key and is not offered from here.
    */}
    {settingCondition ? (
      <SetStockUnitConditionModal
        open
        unit={settingCondition}
        onClose={() => {
          setSettingCondition(null);
          reopenDetail();
        }}
        onSaved={() => {
          setSettingCondition(null);
          dropResume();

          // Damaged and under repair still count as HELD and no longer as available, so the
          // group's own totals have just moved.
          refreshMoved();
        }}
      />
    ) : null}

    {/*
      THE STOCK ADJUSTMENT FORM. Mounted only while a row is chosen, so its own open effect
      fetches the item, the facilities and the existing lots once per opening rather than on
      every render of this page.
    */}
    {adjusting ? (
      <StockAdjustmentModal
        open
        target={adjusting}
        onClose={() => setAdjusting(null)}
        onSaved={() => {
          setAdjusting(null);

          // Every figure on this page is derived from the lots, so a correction moves the
          // group the form was opened from — and the cached answers have to go with it. It
          // moves THAT group and no other, which is what the scoped refresh says.
          refreshMoved();
        }}
      />
    ) : null}

    {/*
      THE STOCK UNIT REGISTRATION. Mounted only while a row is chosen, so its facility feed is
      fetched once per opening and its grid resets to one blank row between deliveries.
    */}
    {registering ? (
      <StockUnitsRegisterModal
        open
        target={registering}
        onClose={() => setRegistering(null)}
        onSaved={() => {
          setRegistering(null);

          // Every figure on this page is derived from the units, so a registration moves the
          // group the form was opened from — and the cached answers have to go with it.
          refreshMoved();
        }}
      />
    ) : null}

    {/*
      THE LICENCE REGISTRATION. Mounted only while a row is chosen, so its facility and staff
      feeds are fetched once per opening and its grid resets to one blank row between purchases.
    */}
    {registeringLicences ? (
      <LicenseRegisterModal
        open
        target={registeringLicences}
        onClose={() => setRegisteringLicences(null)}
        onSaved={() => {
          setRegisteringLicences(null);

          // A licence seat is a stock unit, so every figure on the ITEM'S OWN ROW moves with
          // it — and the cached answers have to go with them. Scoped like its two siblings: it
          // reloaded every group until 29 Sep 2026, and the only reason it could not be scoped
          // was that the hand-off above named no type.
          refreshMoved();
        }}
      />
    ) : null}

    {/*
      DELETE TYPE, and the body states what it actually does — because in this product it does
      LESS than the label suggests, and the reader would otherwise press it and see nothing
      change. Retiring a catalog row stops it being OFFERED; it does not rewrite what existing
      records say they are, so every item filed under it keeps its name and stays in this list.

      Both safeguards, because it is destructive: the tick proves somebody clicked, and the
      password proves it is still the person who signed in.
    */}
    {/*
      REBUILD — a confirmation, because it is a statement over every item in the system and the
      reader should see what it does before firing it.

      NOT `danger` and NOT `requireCredential`: it destroys nothing. The lots, the units, the
      issuance lines and the ledger are the truth and are not touched, so the worst outcome is
      that correct figures are recomputed to the same correct figures — which is a long way from
      the password ceremony a mass delete earns, and ceremony on something harmless is ceremony
      people learn to click through.
    */}
    {confirmRebuild ? (
      <ConfirmDialog
        open
        onClose={() => {
          setConfirmRebuild(false);
          setError(null);
        }}
        onConfirm={() => void runRebuild()}
        title="Rebuild stock figures"
        requireConfirm
        loading={rebuilding}
        id="stock-levels-rebuild"
        body={
          <>
            {error ? (
              <p className="mb-2 text-xs text-red-700" role="alert">
                {error.message}
              </p>
            ) : null}
            Every item's stored figures are recomputed from its lots, units, issuances and
            ledger. Nothing is deleted and no stock moves — those records are the source, and
            this only brings the stored totals back into step with them. Reach for it when a
            figure here disagrees with the records behind it.
          </>
        }
      />
    ) : null}

    {deletingType ? (
      <ConfirmDialog
        open
        onClose={() => {
          setDeletingType(null);
          setError(null);
        }}
        onConfirm={() => void runDeleteType()}
        title="Delete item type"
        danger
        requireConfirm
        requireCredential
        loading={deleting}
        id="stock-levels-delete-type"
        body={
          <>
            {/* A failed delete says so HERE, in the dialog the reader is still looking at —
                closing it to find the message elsewhere is how a failure goes unread. */}
            {error ? (
              <p className="mb-2 text-xs text-red-700" role="alert">
                {error.message}
              </p>
            ) : null}

            {deletingType.items > 0
              ? `"${deletingType.name}" stops being offered on new items. The ${deletingType.items} ${
                  deletingType.items === 1 ? 'item' : 'items'
                } already filed under it keep the name and stay in this list — deleting a type does not delete what was recorded with it. It can be restored from Inventory → Settings → Item Types.`
              : `"${deletingType.name}" stops being offered on new items. Nothing is filed under it. It can be restored from Inventory → Settings → Item Types.`}
          </>
        }
      />
    ) : null}
    </>
  );
}

/**
 * The header bands, in column order.
 *
 * The item's own identity columns sit OUTSIDE every band deliberately — they are what the row
 * is, not a family of figures, and a heading over them would be naming the obvious. They span
 * both header rows instead.
 *
 * **ISSUED is drawn and carries no data.** Product call, 18 Sep 2026, reversing the earlier
 * call that left it out: the band matches the source system's screen and every cell in it is an
 * EM DASH until issuance and lending are built. The em dash is what makes that honest — a zero
 * would claim nothing is on loan, which is a fact about the stock; an em dash says the product
 * cannot answer, which is a fact about the product. `Dispensed` is the pharmacy one and is
 * included for the same reason the other three are.
 */
/**
 * The header bands, and — from 20 Sep 2026 — the groups the settings cog offers to put away.
 *
 * ONE declaration for both, which is the point: a band the reader can hide and a band the table
 * draws are the same run of columns, and two lists would be two places for a new column to go
 * missing from. `<StockLevelsIndexTable>` builds its runs by walking the COLUMNS, so a group the
 * shell has dropped simply draws no band — nothing had to be told.
 *
 * The `key` is stable and separate from the label, because the label is what a reader sees and
 * the key is what their preference is stored under. Renaming a band must not silently bring back
 * columns somebody put away.
 */
/**
 * The column groups — what the header bands draw, and what the settings cog offers as ticks.
 *
 * ONE declaration for both, which is what stops the two lists drifting: a group the reader put
 * away simply has no columns left for the table to band.
 *
 * <b>FOUR of these hold a single column</b> — Investment, Near expiry, Latest movement and
 * Stock status — and they are here because a column in NO group can never be hidden, which is
 * that rule read correctly rather than an exception to it. They draw no band: a run of one
 * collapses into the tall spanning cell an unbanded column already gets, so the reader gains a
 * tick without the header gaining a heading over its own heading. See `buildHeaderRuns`.
 *
 * <b>Investment therefore LEFT the Summary band on 21 Sep 2026</b>, which is a visible change
 * and the one trade this makes. The reference's own column map files it under SUMMARY; it is
 * the single money column on the table and the one a reader most often wants out of the way,
 * and a column cannot sit in two groups without the two disagreeing about whether it is drawn.
 * Putting it back is moving one string — and costs the reader the ability to hide it alone.
 *
 * Declared in COLUMN order, because the cog lists them in this order and a reader ticking boxes
 * is looking at the table.
 */
const BANDS: { key: string; label: string; keys: string[]; emphasis?: boolean }[] = [

  /*
    BOXED — the one band drawn a step above the rest. Product call, 29 Sep 2026.

    It is what the table is opened for: what is available, what is out, what is gone. At the
    shared boundary tone it was one of nine groups, and a reader scanning twenty-five figures
    had nothing telling them where to look first.
  */
  {
    key: 'summary',
    label: 'Summary',
    keys: ['investment', 'available', 'unusable', 'issued', 'writtenOff'],
    emphasis: true,
  },
  { key: 'issued', label: 'Issued', keys: ['onLoan', 'assigned', 'sold', 'dispensed'] },

  /*
    A BAND OF ONE, and that is the table's own convention rather than an exception to the
    roster's "a band over one column says nothing its header does not". Three bands here
    already hold a single column whose label repeats its header — Near expiry, Latest movement,
    Stock status — because a band is what makes a column HIDEABLE from the settings cog, and
    the columns left ungrouped are the identity ones a reader never puts away. A quantity
    column that stayed out of `BANDS` would be the one figure on this table nobody could
    dismiss.
  */
  { key: 'for-inspection', label: 'For inspection', keys: ['forInspection'] },

  { key: 'unusable', label: 'Unusable', keys: ['expired', 'damaged', 'underRepair', 'quarantined'] },
  { key: 'written-off', label: 'Written off', keys: ['condemned', 'stolen'] },
  { key: 'near-expiry', label: 'Near expiry', keys: ['nearExpiry'] },
  { key: 'lease', label: 'Lease', keys: ['leased', 'leaseExpiring', 'earliestLeaseEnd'] },
  { key: 'threshold', label: 'Threshold', keys: ['reorderLevel', 'maxStockLevel'] },
  { key: 'latest-movement', label: 'Latest movement', keys: ['latestMovementAt'] },
  { key: 'stock-status', label: 'Stock status', keys: ['stockStatus'] },
];

/**
 * A column with no source yet — every cell an em dash.
 *
 * It renders no value rather than a zero, and the distinction is the whole reason the column is
 * allowed to exist: a zero is an answer, and this column has none to give.
 */
function pending<T>(key: string, header: string, width = 105): ColumnDef<T> {
  return {
    key,
    header,
    width,
    align: 'right',
    render: () => <TextCell value={null} mono />,
  };
}

/**
 * A figure that OPENS the stock detail panel, where there is something behind it to open.
 *
 * <b>Live only above zero, and only on a real item's row.</b> A zero has nothing to show —
 * the panel would open on an empty list — so it stays plain text, which also keeps the
 * muted-zero rule intact: the dimmed figures are exactly the inert ones, and the eye already
 * reads them as nothing to act on. A GROUP row and the page TOTAL are refused for a different
 * reason: the panel is per ITEM, and a group already has its disclosure.
 *
 * <b>It does NOT take the anchor blue.</b> That tone is scoped to the row anchor by
 * `AnchorCellContext` precisely so it cannot spread, and a table of twenty blue figures
 * would have no anchor left. What says these are live is the HAND CURSOR, which every button
 * gets from the base rule, and an underline on hover — the treatment this product gives
 * everything clickable that is not the row anchor. The text itself is untouched, so `mono`,
 * `numeric`, the muted zero and the one `strong` column all survive.
 *
 * <b>The accessible name CONTAINS the visible figure</b>, rather than replacing it: a bare
 * `aria-label` reading "open stock detail" over a button showing 16 is what WCAG 2.5.3
 * forbids, and it leaves voice control naming something nobody can see.
 */
function Figure({
  level,
  live,
  label,
  children,
}: {
  level: StockLevelListItem;
  live: boolean;
  /** The figure as it READS, so the accessible name can contain it. */
  label: string;
  children: ReactNode;
}) {
  const open = useContext(StockFigureContext);

  if (!live || open === null || level.isGroupTotal) return <>{children}</>;

  return (
    <button
      type="button"
      onClick={() => open(level)}
      aria-label={`${label} — open stock detail`}
      className="block w-full rounded text-right hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-300"
    >
      {children}
    </button>
  );
}
/** Every quantity column is the same shape, so it is written once. */
function quantity<T extends StockLevelListItem>(
  key: string,
  header: string,
  value: (level: T) => number,
  options: { sortable?: boolean; width?: number; perItem?: boolean; strong?: boolean } = {},
): ColumnDef<T> {
  return {
    key,
    header,
    width: options.width ?? 105,
    align: 'right',
    sortable: options.sortable,
    // `numeric`, so a zero dims — in a table this wide the figures worth reading are the ones
    // that are not zero, and dimming the rest lets the eye find them without reading it.
    //
    // `perItem` refuses the cell on a GROUP row: a threshold belongs to one item, and summing
    // two of them produces a number nothing reorders against. An em dash says the group cannot
    // answer, where a figure would be an answer it does not have — and the reader expands to
    // see each item's own.
    /*
      `strong` is the ONE column on this table drawn heavier than its neighbours, and it is
      the anchor of its band rather than a second emphasis: a table where several figures
      shout has none. It is weight alone — no tone, no size — so a muted zero still mutes,
      which is what keeps a column of quantities scannable.
    */
    render: (level) =>
      options.perItem && level.isGroupTotal ? (
        <TextCell value={null} mono numeric />
      ) : (
        /*
          ABOVE ZERO it opens the stock detail panel — see <Figure>. A zero stays plain text,
          which is what keeps the dimmed figures and the inert ones the same set.
        */
        <Figure level={level} live={value(level) > 0} label={String(value(level))}>
          <TextCell
            value={String(value(level))}
            mono
            numeric
            strong={options.strong}
          />
        </Figure>
      ),
  };
}

/** The dot column's tone, which must agree with the status pill at the end of the row. */
const DOT_TONE: Record<StockLevelListItem['stockStatus'], string> = {
  [STOCK_STATUS.outOfStock]: 'bg-red-500',
  [STOCK_STATUS.low]: 'bg-red-400',
  [STOCK_STATUS.adequate]: 'bg-blue-500',
  [STOCK_STATUS.over]: 'bg-slate-400',

  // The palest step on the ramp, because it is the one state that is NOT a claim about the
  // stock — it says the question does not apply. A tone with any weight would read as one.
  [STOCK_STATUS.notTracked]: 'bg-slate-300',
};

// The mapping moved to `stockTypes.ts` on 20 Sep 2026, when the stock detail panel became a
// second surface drawing this pill. Two copies of one state's tone is the drift a shared
// definition exists to prevent — see `stockStatusTone`.
const statusTone = stockStatusTone;

/**
 * What a CHILD row is titled by — maker, make and model, falling back to the item name.
 *
 * The table groups by item type, so the group row above already states the name: titling the
 * child with it too printed one word twice, which a group holding a single item made plain —
 * `Laptop` over `Laptop`. Brand and model are what distinguish two items of one governed
 * name, which is the question the child row exists to answer.
 *
 * **Joined by a SPACE, not the product's ` — `.** That separator joins a qualifier to a name
 * — `College — University of the Philippines` — where these three are one product's name as
 * anybody writes it: `Lenovo ThinkPad X1 Gen 11`. Em dashes would read as separate facts.
 *
 * **The NAME is the fallback, never an em dash.** All three columns are optional, so a row can
 * carry any of them or none; the anchor is the row's click target and its identity, and
 * a blank title is a row nobody can open or tell apart. An em dash is right for a cell stating
 * an absent value and wrong for the thing naming the record.
 */
function itemTitle(level: StockLevelListItem): string {
  /*
    THE JOIN IS `lib/itemIdentity`'s, not this file's — 29 Sep 2026, when the purchase
    request's item picker became the second surface to draw it. The reasoning above is kept
    here because this is the screen it was decided on; the arithmetic is shared so the two
    cannot write one product two ways.
  */
  return itemIdentityOrName(level, level.name);
}

const COLUMNS: ColumnDef<StockLevelListItem>[] = [
  {
    /*
      The ANCHOR, and it is a STACKED cell — brand and model over `unit · SKU`. That is the
      shape the dedicated table exists for: `<StackedCell secondary>` is refused inside the
      shared index table precisely so it cannot spread, and here it is the row's identity,
      because a SKU alone does not say what the thing is.
    */
    key: 'name',
    header: 'Item',
    width: 300,

    /*
      NOT `flex`, and that is a consequence of freezing it rather than a preference. A soft-flex
      column is rendered WIDTH-LESS so it can absorb the table's slack — and a frozen column's
      neighbour is positioned from its width, so a width nobody can read would pin the status
      dot on top of the name. The slack moved to the last column instead; see `stockStatus`.
    */
    sortable: true,
    summary: true,
    /*
      The SECONDARY carries the UNIT and then the ITEM NUMBER — `pc · ITEM-UMU9KI`.

      <b>It was split into a column of its own for a few hours on 22 Sep 2026 and put back the
      same day.</b> The argument for splitting was the standing one — a value in a stacked
      secondary has no header, no width entry, no sort and no place in the Headers cog — and it
      lost to the row being easier to read with the number under the name it belongs to. Product
      call.

      So this is the documented exception the anchor already is, carrying one more value: the
      table refuses `<StackedCell secondary>` everywhere else, and this surface is dedicated.
      The cost is unchanged and worth restating — the number cannot be sorted on — and the
      SEARCH still reaches it, which is what keeps it findable.
    */
    render: (level) => (
      <StackedCell
        primary={itemTitle(level)}
        secondary={[level.unitCode, level.sku].filter(Boolean).join(' · ')}
      />
    ),
  },
  {
    /*
      The STOCK STATUS as a dot, with no header — the source system's own column, and the one
      piece of the row a reader scans down rather than reads. The full word is in its own
      column at the end, so this is a locator rather than the only statement of the state.
    */
    key: 'stockDot',
    header: '',
    width: 44,
    render: (level) => (
      <Tooltip label={STOCK_STATUS_LABEL[level.stockStatus]}>
        <span
          className={`block h-2.5 w-2.5 rounded-full ${DOT_TONE[level.stockStatus]}`}
          aria-label={STOCK_STATUS_LABEL[level.stockStatus]}
        />
      </Tooltip>
    ),
  },

  // ── Summary ────────────────────────────────────────────────────────────────────────
  {
    /*
      QUANTITY over VALUE, which is the source system's own cell and is one fact rather than
      two: what is OWNED, and what it is worth. The money is null where nothing is priced — an
      em dash, never a zero, because a zero would claim the stock is worthless rather than
      unpriced.

      <b>THE QUANTITY IS EVERY REGISTERED UNIT, and it read ON HAND until 29 Sep 2026.</b> That
      was the defect a reader caught by doing the arithmetic the band invites: the row said 4
      registered, 3 issued, 1 unusable — and 3 available, which cannot be true of four things.
      On-hand excludes issued stock, so the four columns beside it summed to 7 against a leading
      4, and the money omitted the two most expensive units for the same reason: they were out.

      The source states the identity in its own tooltip — <i>"Investment = Available + Unusable +
      Issued + Written Off"</i> — and its cell renders `registeredUnits`. The SHAPE of this cell
      was captured and the FIGURE was not.

      ── THE REGISTERED COLUMN WAS MERGED INTO THIS ONE, 06 Oct 2026 ──────────────────────

      **Product call, in the reader's own words:** *lets merge. owned real properties will be
      represented by investment count.* The two columns never appeared together on a stock tab
      anyway — `STOCK_COLUMN_KEYS` dropped `registered` and `PROPERTY_COLUMN_KEYS` dropped this
      one — so the merge costs a column only on All, where each kind now fills the cell it can
      honestly fill instead of printing a zero in the other.

      **THE TWO FIGURES STAY SEPARATE FIELDS**, and only the CELL merges. Folding properties
      into `registeredUnits` would have made the band's own stated identity false —
      `registeredUnits = available + unusable + issued + writtenOff` — on exactly the tab where
      both kinds sit in one table and a reader can do the arithmetic. One cell reading two
      fields cannot disagree with itself; one field counting two things can.

      **THE MONEY IS THE REASON THE MERGE IS AN IMPROVEMENT rather than a saving.** A property
      item carries no batch and no unit, so this cell read a permanent em dash while
      `RealProperty.AcquisitionCost` sat in the register unread. `StockLevelMaintenance` now
      counts it as a third source, so the count finally has a value beside it.

      **WHAT WAS GIVEN UP:** `registered` carried `permission: 'real-properties.read'` and this
      column carries none, so a stock reader without that key now sees the property count where
      they previously saw no column. A column gate is per COLUMN, not per row, so the two could
      not both survive the merge. It is a UI gate either way — the field has always shipped on
      the row, which `StockLevelListItem.registeredProperties` says in as many words — and the
      product has already ruled a count of registrations is not a value withheld server-side.
    */
    key: 'investment',
    header: 'Investment',
    width: 150,
    align: 'right',
    /*
      ONE button over BOTH lines, because they are one figure: the tally and what it is worth.
      Two would make the money a separate target for the same panel, and a reader pressing
      either half means the same thing.
    */
    render: (level) => {
      /*
        WHICH TALLY THIS KIND CAN HONESTLY STATE. A property is registered rather than received,
        so it has no units; everything else has no properties. The money line is one figure for
        both, because `Investment` already sums all three sources.
      */
      const property = level.kind === ITEM_KIND.realProperty;
      const tally = property ? level.registeredProperties : level.registeredUnits;

      return (
      <Figure
        level={level}
        /*
          A PROPERTY ROW IS NEVER LIVE. The panel behind this button breaks a tally down into
          lots and units, and a property has neither — opening it on a count of buildings would
          show an empty panel under a non-zero figure, which is the dead control the hide rule
          forbids. The register is where a property is looked at, and the anchor already goes
          to the item.
        */
        live={!property && tally > 0}
        label={String(tally)}
      >
        <div className="text-right">
          <TextCell value={String(tally)} mono numeric />
          <span className="block font-mono text-[11px] tabular-nums text-slate-400">
            {level.investment === null ? '—' : formatMoney(level.investment)}
          </span>
        </div>
      </Figure>
      );
    },
  },
  /*
    THE BAND READS IN THE SOURCE'S ORDER — Investment · Unusable · Issued · Written off ·
    Available — which is what makes it legible as the identity it is: the tally leads, the three
    deductions follow, and what is left to issue closes it.

    <b>THIS REVERSES THE CALL OF 28 Sep 2026</b>, which put Available first so the number a
    reader opens the screen for was not at the far end of four columns that are usually zero.
    That argument is a good one and it is stated here rather than deleted: the cost of the
    source's order is that Available moved from first to last. What bought the reversal is that
    the band now CLOSES — with the tally at its head the four columns are visibly a breakdown of
    it, and a reader who adds them gets the leading figure instead of a contradiction.

    Available is still the one figure drawn with weight, wherever it sits.
  */
  /*
    AVAILABLE SITS SECOND, directly after the tally it is the largest part of — product call,
    02 Oct 2026, in the reader's own words: *place the available column before unusable*.

    <b>THIS IS THE THIRD POSITION THIS COLUMN HAS HELD, and all three are recorded rather than
    tidied away</b>, because each was right about something the next one gave up:

      28 Sep  FIRST          the figure the screen is opened for, not at the far end of four
                             columns that are usually zero
      29 Sep  LAST           the SOURCE's own order, which makes the band CLOSE — the four
                             columns read as a breakdown of the tally at their head
      02 Oct  SECOND         what the reader asked for, and it keeps both halves: the figure is
                             next to the tally rather than five columns away, and the band still
                             closes because Investment still leads it

    <b>The cost is that the band no longer reads in the source's order</b>, which is the one
    thing the 29 Sep call bought. The arithmetic is untouched — the four still sum to the
    leading figure whichever order they are printed in — so what is given up is the capture, not
    the sum. Said out loud because a reader comparing the two screens will otherwise read it as
    drift.

    Available is still the one figure drawn with weight, wherever it sits.
  */
  quantity('available', 'Available', (l) => l.available, { sortable: true, strong: true }),
  quantity('unusable', 'Unusable', (l) => l.unusable),

  // DERIVED from the two real figures beside it rather than sent again — issued is what is out
  // and expected back, which is exactly on loan plus assigned. A third wire field would be one
  // fact in two slots and a chance for them to disagree.
  quantity('issued', 'Issued', (l) => l.onLoan + l.assigned),
  quantity('writtenOff', 'Written off', (l) => l.writtenOff, { width: 115 }),

  /*
    ── Issued ───────────────────────────────────────────────────────────────

    THREE of the four were dashes on every row until 21 Sep 2026, when Issuances landed and
    made them answerable. Two are real now. The other two are dashes for DIFFERENT reasons,
    and the difference is worth keeping straight:

    SOLD has no source at all — `StockUnitStatus` 3 and 4 are reserved for the source system's
    Sold and Transferred, and no record in this product writes either. It is the original case:
    the product cannot answer.

    DISPENSED could be filled and deliberately is not. `IssuanceType.Dispense` exists and the
    module writes it, but a dispense is CONSUMPTION — the stock is gone, not out — so the only
    figure available is cumulative. Every other column in this band and the ones around it is a
    current holding that goes down as stock comes back; a cumulative total that only ever rises
    would be the one number here that means something different, under a heading that says they
    are alike. Filling it is a one-line change the day somebody wants consumption on this
    screen, and it belongs in a band of its own when they do.
  */
  quantity('onLoan', 'On loan', (l) => l.onLoan),
  quantity('assigned', 'Assigned', (l) => l.assigned),
  pending<StockLevelListItem>('sold', 'Sold'),

  /*
    DISPENSED IS REAL, on the one tab that draws it — product call, 28 Sep 2026.

    The paragraph above still explains why it was an em dash, and what changed is not the
    argument but the column’s own tab list: it is dropped from Equipment, Digital, Leased and
    Real Property, so a cumulative total no longer sits beside four current holdings claiming
    to be like them. On CONSUMABLES it is the figure the screen is opened for.

    SOLD stays pending beside it, and the two are still different absences: nothing writes a
    sale at all, where this one had a source all along.
  */
  quantity('dispensed', 'Dispensed', (l) => l.dispensed, { width: 115 }),

  /*
    FOR INSPECTION — delivered, and not yet stock. Product call, 01 Oct 2026, in the reader's
    own words: *when an item is officially inspected only then it should be counted in the
    stock level, if not, they should be labelled in the stock level as "For Inspection", add a
    new column in the stock level.*

    THE FIRST HALF WAS ALREADY TRUE, which is worth saying because it changes what this column
    is for. Stock is created at REGISTRATION and registration is refused while any line is
    uninspected, so nothing unverified has ever reached a lot or a unit — every other figure on
    this row was correct. What was missing is that the quantity standing on the dock appeared
    NOWHERE, on the one screen whose job is to say where stock is.

    IT IS NOT THE REFERENCE'S "For Delivery", and the name is deliberately not borrowed. That
    column is `onOrder` — *open PO quantity (Ordered − Received) on Sent + Partially-Received
    purchase orders* — which is the stage BEFORE delivery and is not built here. The pipeline
    reads ordered → for delivery → delivered → FOR INSPECTION → registered → stock, and the
    reference has no equivalent of this one because its goods receipt stocks in regardless.

    NO BAND, and that is the band rule rather than an omission: a band over one column says
    nothing its own header does not, which is the argument the department roster already makes
    against a lone band. It renders as a tall cell between Issued and Unusable — the slot the
    reference gives its own inbound column.

    IT IS NOT `strong`. Available is the one figure on this table drawn heavier than its
    neighbours, and a table where two figures shout has none.

    AND IT IS NOT `perItem`: a group genuinely owns this one, because what is awaiting
    inspection under a governed name is the sum of what is awaiting inspection for each item
    filed beneath it. The server sums it in `GroupFiguresAsync` rather than the browser walking
    children it may never have fetched.

    THE COST, stated rather than discovered: an item whose ONLY stock is on the dock still
    reads "Out of stock" here — red dot, red pill, a row in the out-of-stock tile and the tab's
    attention badge. Making "For Inspection" a sixth `StockStatus` was put up and declined; it
    stays additive if that is ever revisited.
  */
  quantity('forInspection', 'For inspection', (l) => l.forInspection, { width: 125 }),

  // ── Unusable ───────────────────────────────────────────────────────────────────────
  quantity('expired', 'Expired', (l) => l.expired),
  quantity('damaged', 'Damaged', (l) => l.damaged),
  quantity('underRepair', 'Under repair', (l) => l.underRepair, { width: 120 }),
  quantity('quarantined', 'Quarantined', (l) => l.quarantined, { width: 120 }),

  // ── Written off ────────────────────────────────────────────────────────────────────
  quantity('condemned', 'Condemned', (l) => l.condemned, { width: 115 }),
  quantity('stolen', 'Stolen', (l) => l.stolen),

  // ── Near expiry ────────────────────────────────────────────────────────────────────
  {
    /*
      Headed with the ITEM's own window rather than one number, because the window is per item
      — a vaccine warns at 180 days and a reagent at 30. A single header would be wrong for
      most rows, so the days travel in the cell.
    */
    key: 'nearExpiry',
    header: 'Near expiry',
    width: 130,
    align: 'right',
    /*
      A GROUP states the COUNT and not the window. The count is a real sum; the window is not
      the group's to state — this column's own header argument is that a window belongs to one
      item, so borrowing the first child's would label a sum of several with one of them.
    */
    render: (level) =>
      level.nearExpiry === 0 ? (
        <TextCell value="0" mono numeric />
      ) : level.isGroupTotal ? (
        <TextCell value={String(level.nearExpiry)} mono numeric />
      ) : (
        <TextCell value={`${level.nearExpiry} ≤${level.nearExpiryDays}d`} mono />
      ),
  },

  // ── Lease ──────────────────────────────────────────────────────────────────────────
  {
    /*
      LEASED & RENTED — the TAB of that name, read as a column. Product call, 06 Oct 2026, in
      the reader's own words: *remove the Leases & rented, instead make it a column and a card*.

      **What it answers that the tab could not.** The tab crossed the kind and tenure axes to
      hold "anything leased", which meant a reader looking at Equipment had to leave it to find
      out which of those machines are rented. The figure sits on the row instead, so the
      question is answered wherever the reader already is — and the CARD beside the table is
      the way back to a list of nothing but leased rows, which is what the tab was for.

      **It counts the THING, not the catalog row**, which is the one place it departs from the
      tab it replaces. The tab selected on `InventoryItem.Ownership` — one answer for every
      unit under an item — where a tenure is actually recorded per unit and per property. An
      item whose own row says Owned and which holds one rented van reports that van here. That
      is the same reading the two lease ALERTS already take one column along, and the mismatch
      it exposes is the row most worth seeing.

      It leads the Lease band because it says whether the two figures after it can apply at
      all: a row reading zero here has nothing to renew.
    */
    key: 'leased',
    header: 'Leased & rented',
    width: 140,
    align: 'right',

    /*
      NOT `quantity()`, and the reason is the half of that factory this column must not have:
      above zero its cell is a `<Figure>` button opening `<StockDetailModal>`, which breaks a
      tally into LOTS AND UNITS. A property has neither, so on the Real Property tab — where
      this column is one of four — every non-zero figure would open an empty panel. That is the
      dead control the Investment cell already documents, and the factory has no way to refuse
      it per row.

      Everything else the factory would have given is written out here: right-aligned, mono,
      and `numeric` so a zero dims.

      NOT SORTABLE, because `SortableFields` does not carry it — a sort the server cannot reach
      falls back silently, which reads as broken.

      A real SUM on a group: a unit and a property each count one, so adding two groups adds
      the same things. Nothing is borrowed, so there is no `isGroupTotal` branch to write.
    */
    render: (level) => <TextCell value={String(level.leased)} mono numeric />,
  },
  {
    /*
      HOW MANY leases are running out, and WHEN the soonest one does.

      The reference puts this on its property register, where one row is one building and the
      date is a column. That shape does not reach here: `StockUnit.LeaseEndDate` is per unit,
      so a level row can hold three vans coming back on three days — the count is the fact,
      and the date beside it is the one to act on first.

      Unlike Near expiry this header CAN state its window, because the window is flat rather
      than per item. It is in the header rather than the cell for exactly that reason.
    */
    key: 'leaseExpiring',
    header: 'Lease ending',
    width: 150,
    align: 'right',
    render: (level) =>
      level.leaseExpiring === 0 && level.leaseExpired === 0 ? (
        <TextCell value="0" mono numeric />
      ) : (
        <TextCell
          value={`${level.leaseExpiring + level.leaseExpired} ≤${level.leaseEndingDays}d`}
          mono
        />
      ),
  },
  {
    /*
      A calendar date, so `<TextCell mono />` through `formatDateOnly` rather than `<DateCell>`
      — a lease ends on a DAY, and the instant path would tell a reader west of UTC it ended
      the day before.

      A GROUP owns this one: the soonest lease end among several items IS the soonest lease
      end, which is the rare column that totals by taking a minimum rather than a sum.
    */
    key: 'earliestLeaseEnd',
    header: 'Lease ends',
    width: 140,
    render: (level) => (
      <TextCell
        value={level.earliestLeaseEnd === null ? null : formatDateOnly(level.earliestLeaseEnd)}
        mono
      />
    ),
  },

  // ── Threshold ──────────────────────────────────────────────────────────────────────
  quantity('reorderLevel', 'Reorder at', (l) => l.reorderLevel, {
    sortable: true,
    width: 115,
    perItem: true,
  }),
  {
    key: 'maxStockLevel',
    header: 'Maximum',
    width: 110,
    align: 'right',
    // An em dash where no ceiling was set — "no maximum" is a different fact from a maximum of
    // zero, and an item with none is never over it.
    // A GROUP cannot own a ceiling either — see `perItem` on `quantity`.
    render: (level) => (
      <TextCell
        value={
          level.isGroupTotal || level.maxStockLevel === null ? null : String(level.maxStockLevel)
        }
        mono
        numeric
      />
    ),
  },

  // ── State ──────────────────────────────────────────────────────────────────────────
  {
    key: 'latestMovementAt',
    header: 'Latest movement',
    width: 180,
    sortable: true,
    // An INSTANT, unlike every date on the batch and unit lists — a movement happens at a
    // moment, so this is `<DateCell>` and shows a time.
    render: (level) =>
      level.latestMovementAt === null ? (
        <TextCell value={null} mono />
      ) : (
        <DateCell value={level.latestMovementAt} />
      ),
  },
  {
    key: 'stockStatus',
    header: 'Stock status',
    width: 140,

    /*
      THE SLACK ABSORBER, from 22 Sep 2026. It is the last column, so a table narrower than its
      panel stretches at the right edge — which is what a trailing spacer would do, without a
      spacer cell in all four row branches.

      It was the ANCHOR's job until the anchor was frozen: a soft-flex column renders width-less
      and a frozen column's neighbour is offset by its width, so the two cannot be the same
      column. Without an absorber the browser distributes the slack across every column, and
      dragging one narrower visibly widens the rest.
    */
    flex: true,
    status: true,
    render: (level) => (
      <StatusText tone={statusTone(level.stockStatus)}>
        {STOCK_STATUS_LABEL[level.stockStatus]}
      </StatusText>
    ),
  },
];

/**
 * The columns whose figures come from the AGING call, by key.
 *
 * `<StockLevelsIndexTable>` draws a SKELETON in these cells until that call lands, and the
 * reason is the stored row rather than taste: `Project` leaves every one of these fields at
 * `default(int)` and `WithAging` is what fills them, so the row the table already holds carries
 * a ZERO in each. A muted zero is exactly what "none expired" looks like, so rendering it while
 * the answer is in flight is a wrong answer rather than an absent one — and the two are
 * indistinguishable on screen, which is what makes the skeleton load-bearing.
 *
 * **WRITTEN OUT rather than derived**, for the reason `PROPERTY_COLUMN_KEYS` is: a column key is
 * not a DTO field name. `stockDot` and `stockStatus` both read `stockStatus`, `nearExpiry` reads
 * two fields and `leaseExpiring` reads three, so nothing about a `ColumnDef` says which half of
 * the row its render came from.
 *
 * **SO A COLUMN OVER AN AGING FIGURE OWES AN ENTRY HERE, in the same change.** Forgetting one is
 * silent in exactly the way this set exists to prevent: the cell renders the figure's zero, the
 * row looks finished, and the real value replaces it a moment later with nothing having said so.
 *
 * `earliestLeaseEnd` is deliberately ABSENT — it is a minimum over dates somebody wrote down, so
 * it is stored and arrives with the row. `delayedReturn` ages and has no column at all; it feeds
 * the alert tile, which reads the summary call.
 */
const AGING_COLUMN_KEYS: ReadonlySet<string> = new Set([
  'stockDot', // stockStatus
  'unusable', // unusable
  'available', // available
  'expired', // expired
  'nearExpiry', // nearExpiry + nearExpiryDays
  'leaseExpiring', // leaseExpiring + leaseExpired + leaseEndingDays
  'stockStatus', // stockStatus + stockStatusLabel
]);

/**
 * Every column — what the four STOCK tabs draw.
 *
 * **Derived from `COLUMNS` rather than written out**, so a column added later appears on all
 * four without anyone remembering. A hand-kept list is a list a new column goes missing from,
 * silently, on every tab at once.
 *
 * **It used to subtract `registered`**, which stopped existing on 06 Oct 2026 when that figure
 * merged into the Investment cell. The filter came out with it rather than being left to
 * exclude a key nothing defines — a subtraction that matches nothing is a rule that has quietly
 * stopped doing anything.
 */
const STOCK_COLUMN_KEYS = COLUMNS.map((column) => column.key);

/**
 * The stock set, less the columns a given KIND can never report.
 *
 * **Product call, 28 Sep 2026.** Every tab drew the same twenty-odd figures, so a reader on
 * Consumables met On loan, a Lease band and a Condemned column that can only ever read zero
 * for a consumable — and a column saying one thing on every row is the failure this product
 * already names twice: the Registered column is withheld from every tab but Real Property,
 * and the alert tiles were filed by tab on 20 Sep for exactly this reason.
 *
 * **DERIVED by subtraction, never written out.** A hand-kept list per tab is five lists a new
 * column goes missing from silently — which is the argument `STOCK_COLUMN_KEYS` is already
 * built on. Naming what each tab DROPS keeps a new column appearing everywhere by default,
 * and makes each removal a sentence somebody has to write down.
 *
 * **The SERVER is unchanged**, deliberately. A tab decides what is worth a COLUMN, never what
 * may be counted or filtered — the same line the tile strip draws, and the reason the rail
 * still offers every axis on every tab.
 */
const stockColumnsWithout = (...dropped: string[]) =>
  STOCK_COLUMN_KEYS.filter((key) => !dropped.includes(key));

/**
 * What a property TYPE can honestly state.
 *
 * Written out rather than derived, because this is a statement about what matters on the tab
 * rather than an exclusion: the anchor, the count, and the two lease figures — which read the
 * REGISTER's own dates, so they are the one stock-shaped column that means something here.
 *
 * **The count is `investment` from 06 Oct 2026**, not `registered`: the two merged, and on this
 * tab the cell states how many properties are registered and what they were acquired for.
 *
 * **`stockStatus` is deliberately absent.** Every row on this tab is "Not tracked", so the
 * column would say one thing on every row — the zero-column failure wearing a word.
 */
const PROPERTY_COLUMN_KEYS = [
  'name',
  'investment',

  // A building is rented as readily as it is bought, and the register records which — so this
  // is one of the two stock-shaped columns that mean something on this tab.
  'leased',
  'leaseExpiring',
  'earliestLeaseEnd',
];

/**
 * The registry's tab strip — the reference's own taxonomy, and NOT the kind enum.
 *
 * **EVERY TAB SELECTS ON KIND ALONE from 06 Oct 2026**, and the tenure axis left the strip
 * with the Leased & Rented tab — product call, in the reader's own words: *remove the Leases &
 * rented, instead make it a column and a card*. Every slice still NAMES both tenures, because
 * a slice replaces only the axes it names and one naming kind alone would inherit whatever
 * tenure the previous tab left behind.
 *
 * <b>That tab was the reason `NavSlice` crossed two axes at all</b> — "a leased van and a
 * leased office are the same question" — and the question is now answered by the `leased`
 * column on every row and the Leased & Rented card above the table. What is lost is a list of
 * nothing but leased things reachable in one press from anywhere; what is gained is that a
 * reader on Equipment can see which of those machines are rented without leaving the tab.
 *
 * **ALL LEADS THE STRIP from 29 Sep 2026, and that REVERSES this paragraph.** It read "there
 * is no All tab — a slice set is a closed taxonomy the page declares, and the reference has none
 * either", and both halves of that were true: the reference's strip has five tabs and every item
 * sits on exactly one of them. What overturned it is a product call, and the reason is the one
 * the closed taxonomy could not serve — *what is in the register*, asked without first answering
 * which kind. A reader who does not know the kind had to guess a tab.
 *
 * **It is a REAL SLICE, not an absent one.** `select: {}` would match every state vacuously —
 * `Object.entries({}).every()` is true — so it would be the active tab on every screen and the
 * other five would never light. All therefore NAMES both axes and selects every value on each,
 * which is the one state no narrower slice can also hold: `activeSlice` compares lengths
 * exactly, so four kinds and two tenures is unambiguous.
 *
 * It is declared AFTER `COLUMNS` because four of the five derive their columns from it. A
 * const referencing one declared below it is a module-load error rather than a type error, so
 * the order here is load-bearing.
 */
/**
 * How many children ONE group asks for, and it is the platform’s cap rather than a guess.
 *
 * `PagedResult.MaxPageSize` is 100 and CLAMPS SILENTLY, so a larger number here would not buy
 * a longer list — it would buy a list that stops at 100 with nothing on screen saying so, which
 * is the one thing a collection with no pager must not do. The group row states the overflow
 * instead.
 */
/**
 * The alert tiles the settings cog offers to put away — key and label, in the order they draw.
 *
 * <b>It is written out rather than derived from `<StockLevelAlerts>`'s own list</b>, because
 * that list is built from the COUNTS: deriving it would mean computing every tile's figure to
 * populate a menu nobody has opened. The cost is the one every written-out list carries — a
 * ninth tile owes an entry here in the same change, or it can be drawn and never put away.
 *
 * The labels are the tiles' own, verbatim. A cog that named them differently from the things it
 * hides would be two words for one control, one click apart.
 */
const ALERT_CARDS: { key: string; label: string }[] = [
  { key: 'out-of-stock', label: 'Out of Stock' },
  { key: 'low', label: 'Low' },
  { key: 'near-expiry', label: 'Near Expiry' },
  { key: 'expired', label: 'Expired' },
  { key: 'delayed-return', label: 'Delayed Return' },
  { key: 'stolen', label: 'Stolen' },
  { key: 'leased', label: 'Leased & Rented' },
  { key: 'lease-expiring', label: 'Lease Ending' },
  { key: 'lease-expired', label: 'Lease Expired' },
];

const CHILDREN_PAGE_SIZE = 100;

/**
 * WHICH AXIS the table groups on, from the question being asked.
 *
 * **Real property groups by CATEGORY and everything else by item type**, 06 Oct 2026. A
 * property item's type is the seeded `Real Property` row, so grouping that tab by type puts
 * every property on the books into one group whose header repeats the tab's own name — a
 * header stating nothing, over the one tab where the category IS what a reader scans for. It
 * is the same product call that took the Property Name off the item form: with no identity
 * field left, the category is what tells two property items apart.
 *
 * **Derived from the KIND FACET rather than from the active tab**, so a reader who narrows to
 * real property through the filter rail gets the same grouping as one who pressed the tab. The
 * test is EXACTLY one kind and that kind being real property — a list holding anything else is
 * a list where a category group would mix two kinds under one header.
 *
 * It is read by `levelQuery`, which puts the answer on the query, and the four calls below it
 * all scope their group ids by what the query says. One derivation, so no two of them can
 * disagree about what a group id means.
 */
function groupingAxis(kinds: readonly string[] | undefined): 'type' | 'category' {
  return kinds?.length === 1 && kinds[0] === 'RealProperty' ? 'category' : 'type';
}

const SLICES: NavSlice[] = [
  {
    /*
      ALL — every kind, both tenures, and the tab the page LANDS on.

      `navSlices[0].select` is what seeds the query, so putting it first is what makes the
      register open on everything rather than on one kind. That moves the landing off
      Expendables, which is the stated cost: a reader who always wants one kind now presses its
      tab once. The opposite cost — a reader who wants the whole register having to press four
      tabs and add up — is the one this tab removes.

      **It carries NO `columnKeys`, and that is the honest answer for a tab spanning every
      kind.** Each of the other five names what its kind cannot report; All has no such column,
      because every column here is real for something on it. `registered` is the case to read:
      it is withheld everywhere else for reading zero on every row, and on this tab the property
      rows carry a real count — so the objection does not reach it. The cost is that an
      expendable row shows a muted zero there, which is what a zero means.

      **And NO entry in `TILES_BY_SLICE`**, deliberately rather than by omission: an unknown
      key draws every tile, which is exactly right here and stays right when a ninth tile is
      added. An explicit list would be a second copy of `allTiles` to keep in step.

      **It is absent from `SLICE_KIND` too**, so its Create opens with the kind FREE. That is
      the opposite of the other four, which lock the kind a tab registers — All names no kind,
      so there is nothing to lock and the reader answers it on the form.
    */
    key: 'all',
    label: 'All',
    select: {
      kind: ['Consumable', 'Asset', 'Subscription', 'RealProperty'],
      tenure: ['Owned', 'Leased'],
    },

    /*
      Its badge is the register's whole attention count, and it needed no new machinery:
      `loadBadges` walks SLICES and scopes each call by that slice's own two axes, so naming
      every value counts every item. The server's `ApplyKind` and `ApplyTenure` both take a
      list, so this is one more call rather than a new endpoint.
    */
    badgeFrom: { facet: 'attention', values: ['all'] },
  },

  {
    key: 'consumables',
    label: 'Expendables',
    select: { kind: ['Consumable'], tenure: ['Owned', 'Leased'] },

    /*
      The ATTENTION badge — the reference's own number, and it names the SLICE rather than a
      (kind, tenure) pair. Product call, 28 Sep 2026.

      One call answers one tab now, so the tab is the unit the count is keyed on — where the
      pair was what a single summary had to key ten buckets on to serve five tabs between
      them. A slice spanning two kinds no longer has to name both and sum them, which is what
      drifted the first time Equipment narrowed.

      The page fills the axis from `stockLevelsApi.attention`, one entry as each lands — see
      `loadBadges`. An entry that is not in yet draws NO badge, which is what the shell does
      with a zero anyway, so a tab is silent until it has something to say.
    */
    badgeFrom: { facet: 'attention', values: ['consumables'] },

    /*
      A consumable is DISPENSED rather than lent, and it is not leased — so On loan and the
      whole Lease band read zero on every row. Condemned goes with them: a consumable that
      cannot be used is Expired or Damaged, and condemnation is a decision taken about
      equipment.
    */
    columnKeys: stockColumnsWithout(
      'onLoan',
      'leased',
      'leaseExpiring',
      'earliestLeaseEnd',
      'condemned',
    ),
  },

  /*
    BOTH TENURES AGAIN, 06 Oct 2026 — the one-line change that pairs with removing Leased &
    Rented, read backwards.

    It was narrowed to OWNED only because that tab existed to receive the rest, and taking the
    tab away without widening this would leave a leased forklift on no tab at all — which this
    file has already called worse than one on a tab whose name is slightly wide. Equipment is
    every asset again, and WHICH of them are rented is now the `leased` column and the card
    above the table.
  */
  {
    key: 'equipment',
    label: 'Equipment',
    select: { kind: ['Asset'], tenure: ['Owned', 'Leased'] },
    badgeFrom: { facet: 'attention', values: ['equipment'] },

    /* A laptop is issued and comes back. Nothing about equipment is DISPENSED. */
    columnKeys: stockColumnsWithout('dispensed'),
  },

  {
    key: 'digital',
    label: 'Digital & Subscriptions',
    select: { kind: ['Subscription'], tenure: ['Owned', 'Leased'] },
    badgeFrom: { facet: 'attention', values: ['digital'] },

    /*
      A LICENCE IS NOT A PHYSICAL THING, which is what takes five columns off this tab. It is
      not lent, not dispensed, not damaged and not condemned — a seat is held or it has
      lapsed.

      And it carries no LEASE: a term licence expires on its own term end, which the Near
      expiry band already reports, so the Lease band would read zero on every row while the
      figure a reader wants sits one band along.
    */
    columnKeys: stockColumnsWithout(
      'onLoan',
      'leased',
      'leaseExpiring',
      'earliestLeaseEnd',
      'dispensed',
      'damaged',
      'condemned',
    ),
  },

  {
    /*
      REAL PROPERTY — the last tab, and the one that answers a different question from the
      register beside it.

      **The register lists INSTANCES; this lists KINDS.** Nobody opens a property register to
      read "Office Space × 3", and nobody scans a list of forty buildings to find out how many
      kinds of property are held. The anchor opens the ITEM, exactly as it does on every other
      tab, and the register is one click on from there.

      **It drops every quantity column**, which is what `columnKeys` is for and what the
      reference's own strip does here: a building has no on-hand, no reorder level and no
      investment, so the bands would be twenty em dashes across. What is left is what a property
      TYPE can state — how many are registered, and what is running out.
    */
    key: 'real-property',
    label: 'Real Property',
    select: { kind: ['RealProperty'], tenure: ['Owned', 'Leased'] },
    badgeFrom: { facet: 'attention', values: ['real-property'] },
    columnKeys: PROPERTY_COLUMN_KEYS,
  },
];


/**
 * The kind a tab's Create registers, locked in the form so a registration started from a tab
 * cannot land under another.
 *
 * **ONE tab is absent on purpose**, and the type is `Partial` so that absence is expressible
 * rather than a lookup that silently returns undefined.
 *
 * `all` spans every kind, so there is nothing to lock and the form opens with the field free
 * for the reader to answer. An absence here means "do not lock".
 *
 * `leased` used to be the second, and it went with the Leased & Rented tab on 06 Oct 2026 —
 * a tenure is a column and a card now, not a tab.
 */
const SLICE_KIND: Partial<Record<string, InventoryItemKind>> = {
  consumables: ITEM_KIND.consumable,
  equipment: ITEM_KIND.asset,
  digital: ITEM_KIND.subscription,
  'real-property': ITEM_KIND.realProperty,
};
